/**
 * Состояние терминала.
 *
 * Свечи хранятся дважды по назначению, а не по небрежности:
 *   - `candles` — массив закрытых баров, источник правды для расчётов
 *     и для статус-бара; меняется редко;
 *   - `op` — последняя операция над графиком; график применяет её
 *     императивно, не перерисовывая тысячи баров на каждый тик.
 *
 * Формирующийся бар в `candles` не попадает: ровно как в Core, он живёт
 * отдельно (`partial`) и в историю не записывается.
 */

import { adoptSeen, seenAt, setSeenPersister } from './seen.js';
import type { StickerPose } from '../drawings/sticker-pose.js';
import { alertGeometry, alertShapeOf, conditionFor } from '../drawings/drawing-alerts.js';
import {
  DEFAULT_SETTINGS,
  normalizeSettings,
  normalizeTradePlan,
  priceAlertsFrom,
  resolveThresholds,
  type AssetClass,
  type PriceAlert,
  type InstrumentStats,
  type Signal,
  type Thresholds,
  type Ticker,
  type Timeframe,
  type UserSettings,
} from '@specter/types';
import { create } from 'zustand';
import type { CandleTuple, GapRange, VenueInfo } from '../api/client.js';
import type { ConnectionState } from '../api/stream.js';
import { createInstance } from '../indicators/registry.js';
import { DEFAULT_CHART_STYLE } from '../chart/styles.js';
import { DEFAULT_LOOK, parseLook, type ChartLook } from '../chart/look.js';
import { DEFAULT_SCALE, parseScale, type ScaleSettings } from '../chart/scale.js';
import type { IndicatorInstance, IndicatorParams } from '../indicators/types.js';
import type { BotInfo } from '../api/client.js';
import { defaultParams, findIndicator, sanitizeParams } from '../indicators/registry.js';
import { mergeSignal } from './signal-slice.js';
import { isAlertEvent, mergeAlertLog } from '../alerts/journal.js';
import { journeyFrom, NO_JOURNEY, trimVersions, userRadarsFrom, versionsFrom, type FiboTrade, type Journey, type SystemVersion, type UserRadar } from '@specter/types';
import { botSignalFrom, botSourceFrom, botTradeFrom, NO_MARKS, NO_STUDIO_PROGRESS, specFingerprint, specFromRule, type ProfileMarks, type RuleCondition as StudioCondition, type StudioProgress } from '@specter/types';
import {
  divergenceWatchId,
  divergenceWatchesFrom,
  INDICATOR_EVENTS,
  indicatorWatchId,
  indicatorWatchesFrom,
  zonesWatchesFrom,
  type DivergenceWatch,
  type IndicatorWatch,
  type ZonesWatch,
} from '@specter/rules';
import { DIVERGENCE_ID, divergenceWatchSnapshot, zonesWatchSnapshot } from '../indicators/definitions.js';
import { templateById } from '@specter/rules';
import type {
  BotDefinition,
  Rule,
  RuleSignalMeta,
  SignalBranch,
  SignalConfirmation,
  SignalDirection,
  SignalSpec,
} from '@specter/types';
import type { Drawing, DrawingPoint } from '../drawings/types.js';
import { normalizeDrawing } from '../drawings/types.js';
import { nextDrawingId } from '../drawings/interaction.js';
import { findKind, type DrawingKind } from '../drawings/kinds.js';
import { drawingTemplatesFrom, lookOf, applyLook, type DrawingTemplate } from '../drawings/templates.js';
import type { CursorMode, ToolId } from '../drawings/tools.js';
import type { SyncState } from './settings-sync.js';

export interface Candle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export function fromTuple(t: CandleTuple): Candle {
  return { time: t[0], open: t[1], high: t[2], low: t[3], close: t[4], volume: t[5] };
}

/** Операция, которую графику нужно применить к сериям. */
export type ChartOp =
  | { kind: 'reset'; candles: Candle[] }
  | { kind: 'append'; candle: Candle }
  | { kind: 'live'; candle: Candle };

export interface SymbolOption {
  symbol: string;
  base: string;
  quote: string;
  /** Знаков в цене: из неё получается шаг цены для расчёта в тиках. */
  pricePrecision: number;
  qtyPrecision: number;
  /** Разметка источника: крипта, акция, товар, индекс… */
  assetClass: AssetClass;
  tags: readonly string[];
  /** Дата листинга, unix ms. null — источник её не сообщил. */
  listedAt: number | null;
}

export type LoadState = 'idle' | 'loading' | 'ready' | 'error';

export interface TerminalState {
  venue: string;
  symbol: string;
  timeframe: Timeframe;

  venues: VenueInfo[];
  symbols: SymbolOption[];
  /** Суточные сводки по символу. Пусто, пока их не запросили. */
  tickers: Record<string, Ticker>;
  /** Шапка открытого инструмента. null — источник её не отдаёт. */
  stats: InstrumentStats | null;

  candles: Candle[];
  partial: Candle | null;
  gaps: GapRange[];

  op: ChartOp | null;
  opSeq: number;

  load: LoadState;
  connection: ConnectionState;
  error: string | null;
  /** Идёт подгрузка более старой истории при прокрутке влево. */
  loadingHistory: boolean;
  /** Самый старый бар, который Core смог отдать: дальше тянуть нечего. */
  historyExhausted: boolean;

  /**
   * Индикаторы пользователя.
   *
   * Живут отдельно от инструмента: переключение символа или таймфрейма
   * не сбрасывает набор — это рабочее место человека, а не свойство ряда.
   */
  indicators: IndicatorInstance[];

  /**
   * Стиль графика: свечи, бары, линия…
   *
   * Как и индикаторы, это рабочее место человека, а не свойство ряда:
   * смена инструмента его не сбрасывает.
   */
  chartStyle: string;
  /** Настройки шкалы цены: режим, сторона, метки, линии. */
  scale: ScaleSettings;
  /** Оформление графика: фон, цвета свечей, сетка, объём. */
  look: ChartLook;

  /**
   * Разметка: линии, уровни, зоны, подписи.
   *
   * В сторе лежит разметка всех инструментов, а на график попадает
   * только принадлежащая открытому: фильтр дешевле, чем перезагрузка
   * набора при каждом переключении символа.
   */
  drawings: Drawing[];
  /**
   * Правила пользователя.
   *
   * Как и разметка, лежат все сразу, а на график попадают только
   * принадлежащие открытому инструменту: фильтр дешевле перезагрузки
   * набора при каждом переключении символа.
   */
  specs: SignalSpec[];
  /**
   * Правила, ещё не переехавшие в описания.
   *
   * Держатся отдельно, чтобы не потеряться при сохранении: профиль
   * пишется целиком, и не записать их значило бы стереть у человека то,
   * что он собрал прошлой версией.
   */
  legacyRules: Rule[];
  /**
   * Прошлые состояния правящегося описания.
   *
   * Правка помощника — обычная правка конструктора, и отменяется тем
   * же способом. Отдельная память состояния у помощника означала бы
   * два разных «назад» на одном экране, и человек не знал бы, какое
   * из них сейчас сработает.
   */
  specHistory: SignalSpec[];
  /**
   * Боты, собранные в SPECTER.
   *
   * Отдельно от `bots`: там внешние процессы, которые пришли сами и
   * существуют, пока подключены. Эти живут в профиле и переживают всё.
   */
  customBots: BotDefinition[];
  /**
   * Подписки на уведомления готовых индикаторов.
   *
   * Индикатор на графике считается по тому, что открыто сейчас, и
   * заканчивается вместе с вкладкой. Подписка живёт в профиле: в ней
   * записаны инструмент, таймфрейм и порог, по которым сервер считает
   * и шлёт уведомление, пока человека нет за экраном.
   */
  watches: ZonesWatch[];
  /** Подписки на уведомления по дивергенциям — свой список, свой состав настроек. */
  divergenceWatches: DivergenceWatch[];
  /** Подписки на оповещения остальных индикаторов — см. `INDICATOR_EVENTS`. */
  indicatorWatches: IndicatorWatch[];
  /** Оповещения по цене. Срабатывает их сервер — и при закрытой вкладке. */
  priceAlerts: PriceAlert[];
  /**
   * Правая панель: оповещения и журнал.
   *
   * Открыта ли она — дело этого устройства, а не профиля: на телефоне и
   * на мониторе человек держит экран по-разному.
   */
  alertsPanel: { readonly open: boolean; readonly tab: 'alerts' | 'journal' };
  /** Оповещение, которое сейчас создаётся или правится; `null` — окна нет. */
  alertDraft: PriceAlertDraft | null;
  /**
   * Записи профиля, которые эта версия терминала прочитать не смогла.
   *
   * Индикатор, чьего определения здесь нет, сигнал в форме, которой код
   * ещё не знает, подписка на незнакомом таймфрейме. Показать их нельзя,
   * но и потерять нельзя: профиль сохраняется целиком, и без этого
   * списка первое же сохранение стёрло бы их с сервера. Так и пропадали
   * индикаторы и уведомления после обновлений.
   */
  unread: UnreadEntries;
  /**
   * Правило, которое сейчас собирают.
   *
   * Существует только в памяти: пока не нажато «Сохранить», правила нет
   * ни в профиле, ни в списке. Отметки на графике при этом уже видны —
   * ради них конструктор и стоит поверх графика.
   */
  draftSpec: SignalSpec | null;
  /** Правило, открытое в конструкторе. null — конструктор закрыт. */
  editingSpecId: string | null;
  /**
   * Каким экраном открыт индикатор.
   *
   * `card` — готовый индикатор как вещь: что он ищет, сколько нашёл,
   * что с ним делать дальше. `builder` — сборка блоков.
   *
   * Разделение не техническое. Человек, открывший собранный индикатор,
   * чаще хочет посмотреть, чем править, — и попадать сразу в редактор
   * ему незачем. А тот, кто пришёл править, нажмёт «Изменить» и окажется
   * там же, где был раньше.
   */
  specView: 'card' | 'builder';
  /**
   * Шаг цепочки, выбранный для показа на графике.
   *
   * Человек нажимает «② Слом структуры» — и на графике остаётся то, что
   * дожило до второго шага, а не финальные сигналы. Так цепочку можно
   * проследить своими глазами, а не поверить числу под ней.
   *
   * Живёт в хранилище, а не внутри конструктора, потому что читать его
   * должен график: он рисует отметки, а конструктор их только называет.
   */
  chainFocus: { readonly direction: SignalDirection; readonly index: number } | null;
  /** Индикатор, для которого просят завести бота; null — не просят. */
  botForSpecId: string | null;
  /** Открыть «Моих ботов» сразу на этом боте — просьба из «Мира» после форка или копии. */
  openBotId: string | null;
  /** «Улучшить» Ловца: его форма живёт на странице радара, и радар подбирает просьбу. */
  improveRadarBot: BotDefinition | null;
  /**
   * Просьба показать этот бар на графике.
   *
   * Человек нажал силу сигнала в карточке — график должен доехать туда,
   * а не заставлять искать отметку глазами по тысяче свечей. Число
   * сигналов без возможности на них посмотреть — отчёт, а не терминал.
   *
   * Метка времени просьбы обязательна: без неё повторное нажатие на тот
   * же кружок ничего бы не изменило в хранилище, и график не поехал бы
   * во второй раз.
   */
  focusBar: { readonly time: number; readonly at: number } | null;
  /**
   * Снимок правила из сигнала.
   *
   * Открывается при переходе от сигнала к правилу, если правило с тех
   * пор изменилось: показать текущие условия там, где человек ждёт
   * увидеть породившие сигнал, значило бы соврать. Снимок доступен
   * только для чтения — прошлое не редактируют.
   */
  ruleSnapshot: RuleSignalMeta | null;
  /** Выбранный инструмент разметки. */
  tool: ToolId;
  /**
   * Режим курсора: перекрестие, точка, стрелка, ластик.
   *
   * Отдельно от инструмента: курсор — это то, чем человек смотрит, а
   * инструмент — то, чем рисует. Выбрав линию и вернувшись к курсору,
   * он должен получить тот курсор, который выбрал раньше.
   */
  cursor: CursorMode;
  /** Замер линейкой: живёт до следующего действия, в профиль не идёт. */
  measurement: Drawing | null;
  /** Значок, который поставит инструмент «Иконка». */
  icon: string;
  /** Выделенная фигура — её можно тянуть и удалить. */
  selectedDrawingId: string | null;
  /** Притягивать точки к ценам бара. */
  magnet: boolean;
  drawingsHidden: boolean;
  drawingsLocked: boolean;

  /**
   * Все полученные сигналы, включая непроходящие порог.
   *
   * Порог применяется при отборе к показу, а не при приёме: подвинутый
   * ползунок не должен стирать уже полученные данные.
   */
  signals: Signal[];
  /**
   * Журнал: сработавшие оповещения по всем монетам.
   *
   * Отдельно от `signals`: там сигналы открытого инструмента для
   * графика, а журнал отвечает «что меня будило» — и оповещение по
   * эфиру должно быть видно, пока открыт биткоин.
   */
  alertLog: Signal[];
  /** Сигнал, открытый в карточке. */
  selectedSignalId: string | null;

  /**
   * Профиль пользователя — источник правды для порогов, ботов и
   * уведомлений. Хранится в Core и синхронизируется между устройствами.
   */
  settings: UserSettings;
  syncState: SyncState;
  /** Профиль перечитали из-за чужой правки; `null` — сказать нечего. */
  syncNotice: string | null;
  /** Подключённые боты по данным Core. */
  bots: BotInfo[];

  setInstrument(venue: string, symbol: string, timeframe: Timeframe): void;
  setVenues(venues: VenueInfo[]): void;
  setSymbols(symbols: SymbolOption[]): void;
  setChartStyle(style: string): void;
  /** Правка одной настройки шкалы; остальные остаются как были. */
  setScale(patch: Partial<ScaleSettings>): void;
  /** Правка оформления графика; остальные поля остаются как были. */
  setLook(patch: Partial<ChartLook>): void;

  setTool(tool: ToolId): void;
  setCursor(cursor: CursorMode): void;
  setMeasurement(measurement: Drawing | null): void;
  setIcon(icon: string): void;
  addDrawing(drawing: Drawing, keepTool?: boolean): void;
  /** Рисунок, у которого надо сразу открыть настройки: план сделки после постановки. */
  settingsRequest: string | null;
  clearSettingsRequest(): void;
  /**
   * Надпись, которую сейчас набирают прямо на графике.
   *
   * Новая заметка, сноска, текст или план открываются полем ввода на
   * месте, а не окном настроек, которое закрывало саму заметку.
   */
  textEdit: string | null;
  /** Рисунок, для которого открыто окно оповещения. */
  drawingAlertId: string | null;
  openDrawingAlert(id: string | null): void;
  /** Сетка, для которой открыто окно «Фибо-пульта». */
  fiboPultId: string | null;
  openFiboPult(id: string | null): void;
  /** Настройка из открытого окна — график рисует её, пока сделка не запущена. */
  fiboPreview: { readonly drawingId: string; readonly trade: FiboTrade } | null;
  setFiboPreview(preview: { readonly drawingId: string; readonly trade: FiboTrade } | null): void;
  openTextEdit(id: string): void;
  closeTextEdit(): void;
  /** Создать пустое правило и открыть его в конструкторе. */
  createSpec(): void;
  /** Вернуть описание к состоянию до последней правки. */
  undoSpec(): void;
  /** Черновик из готового шаблона: та же кнопка «сохранить» дальше. */
  createSpecFromTemplate(templateId: string): void;
  /** Превратить черновик в правило. До этого его не существует. */
  commitSpec(): void;
  /** Сохранить готовый индикатор из студии — сразу в «Мои», с карточкой под графиком. */
  addSpec(spec: SignalSpec): void;
  /**
   * Студия индикаторов: создание (`specId: null`) или правка. Пусто —
   * закрыта. Одна на терминал: её открывают и меню, и карточка.
   */
  studio: { readonly specId: string | null } | null;
  openStudio(specId: string | null): void;
  closeStudio(): void;
  /** Выбросить черновик: человек ушёл, ничего не сохранив. */
  discardSpec(): void;
  /** Сохранить собранного бота: создание или правка. */
  saveCustomBot(bot: BotDefinition): void;
  removeCustomBot(id: string): void;
  setCustomBotState(id: string, state: BotDefinition['state']): void;
  updateSpec(id: string, patch: Partial<SignalSpec>): void;
  removeSpec(id: string): void;
  editSpec(id: string | null, view?: 'card' | 'builder'): void;
  /** Выбрать шаг цепочки для показа на графике; null — снять выбор. */
  focusChainStep(focus: { direction: SignalDirection; index: number } | null): void;
  /**
   * Просьба поставить бота на этот индикатор.
   *
   * Панель ботов живёт своей жизнью и о карточке индикатора не знает.
   * Просьба лежит в общем хранилище, панель её подбирает и открывается
   * уже с выбранным сигналом — человеку не приходится искать его в
   * списке заново, помня название.
   */
  askBotFor(specId: string | null): void;
  askOpenBot(botId: string | null): void;
  askImproveRadarBot(bot: BotDefinition | null): void;
  /**
   * Индикатор из студии, переданный в конструктор ботов: «проверить как
   * стратегию». Панель ботов подбирает его, как `botForSpecId`, и
   * открывает конструктор уже с условиями индикатора.
   */
  botDraft: BotDraft | null;
  askConstructor(draft: BotDraft | null): void;
  /** Коллекция студии: изученные идеи и значки — в профиле человека. */
  studioProgress: StudioProgress;
  /** Отметить идеи изученными и выдать значки; уже отмеченное не дублируется. */
  markStudio(patch: { readonly explored?: readonly string[]; readonly badges?: readonly string[] }): void;
  /** Путь человека: выбор начала, миссии, моменты. */
  journey: Journey;
  patchJourney(patch: Partial<Journey>): void;
  /** Миссия выполнена: запомнить и снять её подсказку с экрана. */
  completeMission(id: string): void;
  /** Версии систем: снимки ботов с итогом на истории. */
  versions: readonly SystemVersion[];
  recordVersion(entry: Omit<SystemVersion, 'id' | 'at'>): void;
  /** Доехать графиком до этого бара; null — забыть просьбу. */
  showBar(time: number | null): void;
  /** Показать правило таким, каким оно было в момент сигнала. */
  showRuleSnapshot(snapshot: RuleSignalMeta | null): void;
  updateDrawing(id: string, points: readonly DrawingPoint[]): void;
  /** Размер и поворот стикера, пока его тянут: история запомнена при нажатии. */
  poseDrawing(id: string, pose: StickerPose): void;
  restyleDrawing(id: string, patch: Partial<Drawing>): void;
  removeDrawing(id: string): void;
  selectDrawing(id: string | null): void;
  /** Выделенные рамкой — двигаются, удаляются и копируются разом. */
  selectedDrawingIds: readonly string[];
  selectDrawings(ids: readonly string[]): void;
  removeDrawings(ids: readonly string[]): void;
  /** Сдвиг нескольких фигур — одной записью в историю. */
  moveDrawings(moves: ReadonlyArray<{ id: string; points: readonly DrawingPoint[] }>): void;
  clearDrawings(): void;

  /** История разметки для Ctrl+Z / Ctrl+Y — в памяти вкладки, не в профиле. */
  drawingPast: readonly (readonly Drawing[])[];
  drawingFuture: readonly (readonly Drawing[])[];
  /** Запомнить разметку перед правкой, которую потом можно отменить. */
  rememberDrawings(): void;
  undoDrawings(): void;
  redoDrawings(): void;
  /** Скопированный рисунок и цена инструмента в момент копирования. */
  drawingClipboard: { readonly drawing: Drawing; readonly price: number | null } | null;
  copyDrawing(id: string): void;
  pasteDrawing(): void;
  /** Копия рисунка рядом с ним; возвращает её id. */
  duplicateDrawing(id: string, shift?: { time: number; price: number }): string | null;
  /** Выше или ниже других рисунков. */
  reorderDrawing(id: string, to: 'front' | 'back'): void;
  /** Сдвиг стрелками: на свечи и на цену. */
  nudgeDrawing(id: string, time: number, price: number): void;
  /** Инструмент не отпускается после фигуры — ставить подряд. */
  stickyTool: boolean;
  setStickyTool(sticky: boolean): void;
  /** Шаблоны вида рисунков — см. `drawings/templates.ts`. */
  drawingTemplates: readonly DrawingTemplate[];
  /** Свои цвета палитры — в профиле, чтобы были на любом устройстве. */
  customColors: readonly string[];
  /** Боты, чьи сетки и сделки видны на графике — в профиле, как всё, что человек выбрал. */
  shownBots: readonly string[];
  /** Показать бота на графике или убрать; без второго аргумента — переключить. */
  showBot(id: string, on?: boolean): void;
  /** Свои радары рынка — в профиле: сервер проверяет их и шлёт оповещения. */
  radars: readonly UserRadar[];
  saveRadar(radar: UserRadar): void;
  removeRadar(id: string): void;
  addCustomColor(color: string): void;
  removeCustomColor(color: string): void;
  /** Сохранить вид фигуры шаблоном её инструмента. */
  saveDrawingTemplate(drawingId: string, name: string): void;
  applyDrawingTemplate(drawingId: string, templateId: string): void;
  /** Сделать шаблоном по умолчанию; повторно — снять. */
  toggleDefaultTemplate(templateId: string): void;
  removeDrawingTemplate(templateId: string): void;
  /** Меню рисунка по правой кнопке. */
  drawingMenu: { readonly id: string; readonly x: number; readonly y: number } | null;
  openDrawingMenu(menu: { id: string; x: number; y: number } | null): void;
  toggleDrawingFlag(flag: 'magnet' | 'drawingsHidden' | 'drawingsLocked'): void;
  setTickers(tickers: Record<string, Ticker>): void;
  setStats(stats: InstrumentStats | null): void;
  setConnection(state: ConnectionState): void;
  setError(error: string | null): void;

  resetSeries(candles: Candle[], gaps: GapRange[]): void;
  prependHistory(candles: Candle[], gaps: GapRange[]): void;
  applyClosed(candle: Candle): void;
  applyRevision(candle: Candle): void;
  applyPartial(candle: Candle): void;
  applyGap(gap: GapRange): void;
  setLoadingHistory(loading: boolean): void;

  addIndicator(definitionId: string): void;
  removeIndicator(instanceId: string): void;
  updateIndicatorParams(instanceId: string, params: IndicatorParams): void;
  updateIndicatorColor(instanceId: string, outputKey: string, color: string): void;
  /** Вернуть настройки и цвета экземпляра к умолчаниям определения. */
  resetIndicator(instanceId: string): void;
  toggleIndicator(instanceId: string): void;
  /**
   * Запомнить доли панелей после того, как их потянули.
   *
   * Приходят все сразу: разделитель между двумя панелями меняет обе, и
   * записывать по одной значило бы сохранить половину увиденного.
   */
  rememberPaneShares(shares: Readonly<Record<string, number>>): void;
  /**
   * Включить или выключить уведомления по индикатору на открытом
   * инструменте и заданном таймфрейме.
   *
   * Подписка привязана к тройке «индикатор + инструмент + таймфрейм».
   * Инструмент берётся открытый: уведомления по рынкам, которых человек
   * не открывал, он не просил. А таймфрейм — любой, и это не прихоть:
   * смотреть человек может на часе, а входить по пяти минутам, и ждать
   * от него переключения графика ради галочки — значит заставлять
   * работать за интерфейс.
   */
  toggleZonesWatch(instanceId: string, timeframe: Timeframe): void;
  /** Убрать подписку из списка в настройках уведомлений. */
  removeZonesWatch(watchId: string): void;

  /** Включить или выключить уведомления по дивергенциям на этом таймфрейме. */
  toggleDivergenceWatch(instanceId: string, timeframe: Timeframe): void;
  removeDivergenceWatch(watchId: string): void;

  /** Включить или выключить оповещения индикатора на этом таймфрейме. */
  toggleIndicatorWatch(instanceId: string, timeframe: Timeframe): void;
  removeIndicatorWatch(watchId: string): void;

  /** Сохранить оповещение: новое или поправленное. */
  savePriceAlert(alert: PriceAlert): void;
  /** Остановить или снова запустить. */
  togglePriceAlert(id: string): void;
  removePriceAlert(id: string): void;
  /** Открыть окно оповещения; без аргумента — закрыть. */
  editPriceAlert(draft: PriceAlertDraft | null): void;
  setAlertsPanel(patch: Partial<TerminalState['alertsPanel']>): void;

  setSignals(signals: Signal[]): void;
  applySignal(signal: Signal): void;
  setAlertLog(signals: Signal[]): void;
  selectSignal(id: string | null): void;

  setBots(bots: BotInfo[]): void;
  applyBotStatus(botId: string, status: BotInfo['status']): void;
  /** Заменяет профиль целиком — приход с сервера, без обратной записи. */
  hydrateSettings(settings: UserSettings): void;
  setSyncState(state: SyncState): void;
  /**
   * Сообщение о том, что профиль перечитан из-за чужой правки.
   *
   * Отдельно от `error`: это не поломка, а событие, о котором человек
   * должен узнать — иначе он останется в уверенности, что последние
   * его правки сохранились, хотя вкладка их отбросила.
   */
  setSyncNotice(notice: string | null): void;
  /** Правка профиля пользователем — уезжает на сервер. */
  patchSettings(patch: (current: UserSettings) => UserSettings): void;
  /** Сдвинуть отметки профиля вперёд — назад они не ходят. */
  markProfile(patch: Partial<ProfileMarks>): void;
  /** Дописать накопленные правки профиля на сервер сейчас же. */
  saveNow(): void;
}

/** Куда уходит изменённый профиль. Подставляется при старте приложения. */
let persist: ((settings: UserSettings) => void) | null = null;

export function setSettingsPersister(fn: (settings: UserSettings) => void): void {
  persist = fn;
}

/**
 * Записать профиль немедленно, не дожидаясь задержки.
 *
 * Обычно правки копятся шестьсот миллисекунд: человек тянет ползунок, и
 * писать на сервер каждое промежуточное значение незачем. Но когда он
 * нажал «Сохранить», ждать нечего — кнопка обязана делать ровно то, что
 * обещает, в тот момент, когда её нажали.
 */
let flushNow: (() => void) | null = null;

export function setSettingsFlusher(fn: () => void): void {
  flushNow = fn;
}

const DEFAULT_TIMEFRAME: Timeframe = '1H';

/** Непрочитанные записи профиля по спискам рабочего места. */
export interface UnreadEntries {
  readonly indicators: readonly unknown[];
  readonly drawings: readonly unknown[];
  readonly rules: readonly unknown[];
  readonly signals: readonly unknown[];
  readonly bots: readonly unknown[];
  readonly watches: readonly unknown[];
  readonly divergenceWatches: readonly unknown[];
  readonly indicatorWatches: readonly unknown[];
  readonly drawingTemplates: readonly unknown[];
  readonly alerts: readonly unknown[];
}

const NO_UNREAD: UnreadEntries = {
  indicators: [],
  drawings: [],
  rules: [],
  signals: [],
  bots: [],
  watches: [],
  divergenceWatches: [],
  indicatorWatches: [],
  drawingTemplates: [],
  alerts: [],
};


export const useTerminal = create<TerminalState>((set, get) => ({
  // Пусто до ответа Core. Зашивать сюда 'binance' нельзя: терминал не
  // знает, какие источники подключены, — это решает Core (ТЗ, Принцип 2).
  venue: '',
  symbol: '',
  timeframe: DEFAULT_TIMEFRAME,

  venues: [],
  symbols: [],
  tickers: {},
  stats: null,

  candles: [],
  partial: null,
  gaps: [],

  op: null,
  opSeq: 0,

  load: 'idle',
  connection: 'connecting',
  error: null,
  loadingHistory: false,
  historyExhausted: false,

  indicators: [],
  chartStyle: DEFAULT_CHART_STYLE,
  scale: DEFAULT_SCALE,
  look: DEFAULT_LOOK,
  drawings: [],
  specs: [],
  legacyRules: [],
  specHistory: [],
  customBots: [],
  watches: [],
  divergenceWatches: [],
  indicatorWatches: [],
  priceAlerts: [],
  alertsPanel: loadAlertsPanel(),
  alertDraft: null,
  unread: NO_UNREAD,
  draftSpec: null,
  editingSpecId: null,
  specView: 'card',
  chainFocus: null,
  botForSpecId: null,
  openBotId: null,
  improveRadarBot: null,
  focusBar: null,
  ruleSnapshot: null,
  tool: 'cursor',
  cursor: 'crosshair',
  measurement: null,
  icon: 'anz:rocket',
  selectedDrawingId: null,
  magnet: false,
  drawingsHidden: false,
  drawingsLocked: false,

  signals: [],
  alertLog: [],
  selectedSignalId: null,

  settings: DEFAULT_SETTINGS,
  syncState: 'loading',
  syncNotice: null,
  bots: [],

  setInstrument: (venue, symbol, timeframe) =>
    set((state) => {
      /*
       * Тот же инструмент — не смена инструмента.
       *
       * Без этой проверки повторный вызов с прежними значениями стирал
       * загруженные свечи и ставил «загружаю». Перезагрузку при этом
       * никто не запускал: эффект, который грузит историю, следит за
       * инструментом, а инструмент не менялся. График оставался пустым
       * до обновления страницы.
       *
       * Так и происходило при возврате на график из другого раздела:
       * терминал заново перечитывал список площадок и в конце
       * «выбирал» тот же инструмент — уже поверх загруженных свечей.
       */
      if (state.venue === venue && state.symbol === symbol && state.timeframe === timeframe) {
        return {};
      }

      /*
       * Сигналы принадлежат инструменту, а не таймфрейму.
       *
       * Их и грузят по инструменту целиком — фильтр по TF клиентский.
       * Если стирать их при каждой смене таймфрейма, лента пустеет и
       * остаётся пустой до перезагрузки страницы: повторного запроса
       * никто не делает, потому что инструмент не менялся.
       */
      const sameInstrument = state.venue === venue && state.symbol === symbol;

      return {
        venue,
        symbol,
        timeframe,
        candles: [],
        partial: null,
        gaps: [],
        // Шапка принадлежит инструменту: чужая маркировка хуже пустой.
        stats: sameInstrument ? state.stats : null,
        // Чужие сигналы на графике не место.
        signals: sameInstrument ? state.signals : [],
        selectedSignalId: null,
        load: 'loading',
        error: null,
        historyExhausted: false,
      };
    }),

  setVenues: (venues) => set({ venues }),
  setSymbols: (symbols) => set({ symbols, tickers: {} }),
  setTickers: (tickers) => set({ tickers }),
  setChartStyle: (chartStyle) =>
    set((state) => ({ chartStyle, settings: saveWorkspace(state, { chartStyle }) })),

  setScale: (patch) =>
    set((state) => {
      const scale = { ...state.scale, ...patch };
      return { scale, settings: saveWorkspace(state, { scale }) };
    }),

  setLook: (patch) =>
    set((state) => {
      const look = parseLook({ ...state.look, ...patch });
      return { look, settings: saveWorkspace(state, { look }) };
    }),

  setTool: (tool) =>
    // Смена инструмента снимает выделение и замер: иначе следующий клик
    // по графику двигал бы фигуру, выбранную до этого.
    set({ tool, selectedDrawingId: null, measurement: null }),

  setCursor: (cursor) => set({ cursor, tool: 'cursor' }),

  setMeasurement: (measurement) => set({ measurement }),

  setIcon: (icon) => set({ icon }),

  saveCustomBot: (bot) =>
    set((state) => {
      const exists = state.customBots.some((entry) => entry.id === bot.id);
      const customBots = exists
        ? state.customBots.map((entry) => (entry.id === bot.id ? bot : entry))
        : [...state.customBots, bot];
      return { customBots, settings: saveWorkspace(state, { bots: customBots }) };
    }),

  removeCustomBot: (id) =>
    set((state) => {
      const customBots = state.customBots.filter((bot) => bot.id !== id);
      // Версии удалённого бота уходят вместе с ним: вернуть их больше некуда.
      const versions = state.versions.filter((entry) => entry.objectId !== id);
      return { customBots, versions, settings: saveWorkspace({ ...state, versions }, { bots: customBots }) };
    }),

  setCustomBotState: (id, botState) =>
    set((state) => {
      const customBots = state.customBots.map((bot) =>
        bot.id === id ? { ...bot, state: botState, updatedAt: Date.now() } : bot,
      );
      return { customBots, settings: saveWorkspace(state, { bots: customBots }) };
    }),

  createSpec: () =>
    set((state) => {
      const now = Date.now();
      const spec: SignalSpec = {
        id: `signal-${now.toString(36)}`,
        name: 'Новый сигнал',
        venue: state.venue,
        symbol: state.symbol,
        timeframe: state.timeframe,
        /*
         * Одна ветвь и без направления.
         *
         * «Сообщить» — потому что наблюдение ничего не советует, и
         * подставить сюда LONG значило бы вложить человеку намерение,
         * которого он не высказывал. Стороны он включит сам, и условия
         * при этом сохранятся.
         */
        branches: [
          {
            direction: 'ALERT',
            context: [],
            // Готовый триггер сразу: пустой конструктор пугает, а
            // событие видно на графике с первой секунды.
            trigger: {
              left: { kind: 'indicator', indicator: 'rsi', params: { period: 14 } },
              operator: 'cross_above',
              right: { kind: 'number', value: 30 },
            },
            confirmations: [],
          },
        ],
        threshold: 0,
        version: 1,
        enabled: true,
        notify: false,
        createdAt: now,
        updatedAt: now,
      };
      /*
       * Сигнал не появляется от одного открытия конструктора.
       *
       * Раньше «Новое правило» создавало запись сразу, и человек,
       * заглянувший посмотреть, получал список из полудюжины правил,
       * которых не создавал. Теперь это черновик: он живёт, пока открыт
       * конструктор, и становится сигналом по кнопке.
       */
      // Черновик открывается сборкой: тут нечего рассматривать, тут
      // надо собирать.
      return { draftSpec: spec, editingSpecId: spec.id, specView: 'builder' as const };
    }),

  createSpecFromTemplate: (templateId) =>
    set((state) => {
      const template = templateById(templateId);
      if (!template) return {};

      const now = Date.now();
      const spec = template.build({
        id: `signal-${now.toString(36)}`,
        venue: state.venue,
        symbol: state.symbol,
        timeframe: state.timeframe,
        now,
      });

      /*
       * Сразу в список, а не черновиком, как пустой.
       *
       * Посмотреть шаблон можно на экране перед созданием: там цепочка
       * словами и оговорка. «Создать» после него — решение, а не
       * любопытство. Черновиком шаблон терялся: сборка открывалась под
       * графиком, на телефоне за краем экрана, кнопку «Сохранить» никто
       * не видел, и индикатор исчезал с перезагрузкой — «создал, а в
       * „Мои“ его нет».
       */
      const specs = [...state.specs, spec];
      return {
        specs,
        draftSpec: null,
        editingSpecId: spec.id,
        specView: 'card' as const,
        chainFocus: null,
        settings: saveSpecs(state, specs),
      };
    }),

  /** Сохранить черновик: здесь сигнал и появляется на самом деле. */
  commitSpec: () =>
    set((state) => {
      const draft = state.draftSpec;
      if (!draft) return {};

      const specs = [...state.specs, draft];
      /*
       * После сохранения — карточка, а не пустой экран.
       *
       * Это тот момент, ради которого всё и делалось: у человека
       * появился индикатор. Закрыть за ним панель значило бы отобрать
       * ответ на вопрос «и что теперь» ровно тогда, когда он его задаёт.
       */
      return {
        specs,
        draftSpec: null,
        specView: 'card' as const,
        chainFocus: null,
        settings: saveSpecs(state, specs),
      };
    }),

  discardSpec: () => set({ draftSpec: null, editingSpecId: null }),

  studio: null,
  openStudio: (specId) => set({ studio: { specId } }),
  closeStudio: () => set({ studio: null }),

  addSpec: (spec) =>
    set((state) => {
      const specs = [...state.specs.filter((entry) => entry.id !== spec.id), spec];
      return {
        specs,
        draftSpec: null,
        editingSpecId: spec.id,
        specView: 'card' as const,
        chainFocus: null,
        settings: saveSpecs(state, specs),
      };
    }),

  updateSpec: (id, patch) =>
    set((state) => {
      // Черновик правится отдельно: его ещё нет ни в списке, ни в
      // профиле, и записывать его туда рано.
      if (state.draftSpec && state.draftSpec.id === id) {
        /*
         * Версия черновика не растёт ни от чего.
         *
         * Она защищает уже произошедшие сигналы от правки описания, а у
         * черновика их нет: он ещё не сохранён и не породил ни одного
         * события. Считать правки до сохранения значило бы отдать первую
         * версию сигнала с номером двадцать.
         */
        return {
          draftSpec: { ...state.draftSpec, ...patch, updatedAt: Date.now() },
          specHistory: [...state.specHistory, state.draftSpec].slice(-20),
        };
      }

      const previous = state.specs.find((spec) => spec.id === id);

      const specs = state.specs.map((spec) => {
        if (spec.id !== id) return spec;

        const next = { ...spec, ...patch, updatedAt: Date.now() };
        /*
         * Версия растёт от всего, что меняет результат, — и только от
         * этого. Что именно меняет, знает `specFingerprint`: условия,
         * веса, порог, направление, таймфрейм. Переименование и
         * включение уведомлений результата не меняют, и поднимать из-за
         * них версию значило бы объявить прошлые события чужими.
         */
        const changed = specFingerprint(next) !== specFingerprint(spec);
        return changed ? { ...next, version: spec.version + 1 } : next;
      });

      /*
       * Правка выводит из режима снимка.
       *
       * Человек, пришедший из сигнала и начавший менять условия, работает
       * уже с текущим описанием. Оставить снимок значило бы схлопнуть
       * панель в «только чтение» ровно в тот момент, когда версия
       * выросла, — то есть наказать за первое же действие.
       */
      return {
        specs,
        // Глубина небольшая намеренно: «назад» нужен на несколько шагов
        // после неудачной правки, а не как журнал всей работы.
        specHistory: previous ? [...state.specHistory, previous].slice(-20) : state.specHistory,
        ruleSnapshot: null,
        settings: saveSpecs(state, specs),
      };
    }),

  undoSpec: () =>
    set((state) => {
      const previous = state.specHistory[state.specHistory.length - 1];
      if (!previous) return {};

      /*
       * Черновик отменяется как черновик, сохранённое — как сохранённое.
       *
       * Иначе «назад» на несохранённом описании записал бы его в
       * профиль, и отмена стала бы сохранением.
       */
      if (state.draftSpec && state.draftSpec.id === previous.id) {
        return { draftSpec: previous, specHistory: state.specHistory.slice(0, -1) };
      }

      const specs = state.specs.map((spec) => (spec.id === previous.id ? previous : spec));
      return {
        specs,
        specHistory: state.specHistory.slice(0, -1),
        settings: saveSpecs(state, specs),
      };
    }),

  removeSpec: (id) =>
    set((state) => {
      const specs = state.specs.filter((spec) => spec.id !== id);
      // Версии удалённого индикатора уходят вместе с ним, как у ботов.
      const versions = state.versions.filter((entry) => entry.objectId !== id);
      return {
        specs,
        versions,
        editingSpecId: state.editingSpecId === id ? null : state.editingSpecId,
        settings: saveSpecs({ ...state, versions }, specs),
      };
    }),

  // Открытие живого сигнала всегда сбрасывает снимок: два состояния
  // одного описания на экране одновременно — источник путаницы.
  // Закрытие конструктора снимает и выбранный шаг: оставить его значило
  // бы показывать на графике обрезанную цепочку без всякого объяснения,
  // потому что объяснение стояло в закрытой панели.
  editSpec: (id, view = 'card') =>
    set({
      editingSpecId: id,
      specView: view,
      ruleSnapshot: null,
      draftSpec: null,
      chainFocus: null,
    }),

  focusChainStep: (focus) => set({ chainFocus: focus }),

  askBotFor: (botForSpecId) => set({ botForSpecId }),
  askOpenBot: (openBotId) => set({ openBotId }),
  askImproveRadarBot: (improveRadarBot) => set({ improveRadarBot }),

  botDraft: null,
  askConstructor: (botDraft) => set({ botDraft }),

  studioProgress: NO_STUDIO_PROGRESS,
  markStudio: (patch) =>
    set((state) => {
      const current = state.studioProgress;
      const explored = [...new Set([...current.explored, ...(patch.explored ?? [])])];
      const badges = [...new Set([...current.badges, ...(patch.badges ?? [])])];
      if (explored.length === current.explored.length && badges.length === current.badges.length) return {};
      const studioProgress = { explored, badges };
      // Новый значок — момент пути: профиль потом расскажет, когда он был получен.
      const now = Date.now();
      const fresh = badges.filter((id) => !current.badges.includes(id) && !state.journey.moments.some((mark) => mark.id === id));
      const journey = fresh.length > 0 ? { ...state.journey, moments: [...state.journey.moments, ...fresh.map((id) => ({ id, at: now }))] } : state.journey;
      return { studioProgress, journey, settings: saveWorkspace({ ...state, studioProgress, journey }, {}) };
    }),

  versions: [],
  recordVersion: (entry) =>
    set((state) => {
      const at = Date.now();
      const versions = trimVersions([...state.versions, { ...entry, id: `${entry.objectId}-v${entry.version}-${at.toString(36)}`, at }]);
      return { versions, settings: saveWorkspace({ ...state, versions }, {}) };
    }),

  journey: NO_JOURNEY,
  patchJourney: (patch) =>
    set((state) => {
      const journey = { ...state.journey, ...patch };
      return { journey, settings: saveWorkspace({ ...state, journey }, {}) };
    }),
  completeMission: (id) =>
    set((state) => {
      if (state.journey.missions.some((mark) => mark.id === id)) return {};
      const journey = { ...state.journey, missions: [...state.journey.missions, { id, at: Date.now() }], active: state.journey.active === id ? null : state.journey.active };
      return { journey, settings: saveWorkspace({ ...state, journey }, {}) };
    }),

  showBar: (time) => set({ focusBar: time === null ? null : { time, at: Date.now() } }),


  showRuleSnapshot: (snapshot) =>
    set({ ruleSnapshot: snapshot, editingSpecId: snapshot?.ruleId ?? null }),

  settingsRequest: null,
  clearSettingsRequest: () => set({ settingsRequest: null }),
  textEdit: null,
  drawingAlertId: null,
  openDrawingAlert: (drawingAlertId) => set({ drawingAlertId }),
  fiboPultId: null,
  openFiboPult: (fiboPultId) => set({ fiboPultId, fiboPreview: null }),
  fiboPreview: null,
  setFiboPreview: (fiboPreview) => set({ fiboPreview }),
  openTextEdit: (id) => set({ textEdit: id, selectedDrawingId: id }),
  closeTextEdit: () => set({ textEdit: null }),

  drawingPast: [],
  drawingFuture: [],
  drawingClipboard: null,
  stickyTool: false,
  drawingMenu: null,
  drawingTemplates: [],
  customColors: [],
  radars: [],
  saveRadar: (radar) =>
    set((state) => {
      const radars = state.radars.some((entry) => entry.id === radar.id) ? state.radars.map((entry) => (entry.id === radar.id ? radar : entry)) : [...state.radars, radar];
      return { radars, settings: saveWorkspace({ ...state, radars }, {}) };
    }),
  removeRadar: (id) =>
    set((state) => {
      const radars = state.radars.filter((entry) => entry.id !== id);
      return { radars, settings: saveWorkspace({ ...state, radars }, {}) };
    }),
  shownBots: [],
  showBot: (id, on) =>
    set((state) => {
      const shown = state.shownBots.includes(id);
      const next = on ?? !shown;
      if (next === shown) return {};
      const shownBots = next ? [...state.shownBots, id] : state.shownBots.filter((item) => item !== id);
      return { shownBots, settings: saveWorkspace(state, { shownBots }) };
    }),
  addCustomColor: (color) =>
    set((state) => {
      const solid = color.slice(0, 7).toUpperCase();
      // Уже есть — переносим в конец: свежий цвет ближе к «+».
      const customColors = [...state.customColors.filter((item) => item.toUpperCase() !== solid), solid].slice(-20);
      return { customColors, settings: saveWorkspace(state, { customColors }) };
    }),
  removeCustomColor: (color) =>
    set((state) => {
      const customColors = state.customColors.filter((item) => item.toUpperCase() !== color.toUpperCase());
      return { customColors, settings: saveWorkspace(state, { customColors }) };
    }),

  saveDrawingTemplate: (drawingId, name) =>
    set((state) => {
      const drawing = state.drawings.find((d) => d.id === drawingId);
      const title = name.trim().slice(0, 40);
      if (!drawing || title === '') return {};
      // То же имя у того же инструмента — перезапись, а не второй шаблон.
      const same = state.drawingTemplates.find((t) => t.kind === drawing.kind && t.name === title);
      const template: DrawingTemplate = {
        id: same?.id ?? `tpl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
        kind: drawing.kind,
        name: title,
        look: lookOf(drawing),
        isDefault: same?.isDefault ?? false,
        createdAt: same?.createdAt ?? Date.now(),
      };
      const drawingTemplates = same
        ? state.drawingTemplates.map((t) => (t.id === same.id ? template : t))
        : [...state.drawingTemplates, template];
      return { drawingTemplates, settings: saveWorkspace(state, { drawingTemplates }) };
    }),

  applyDrawingTemplate: (drawingId, templateId) =>
    set((state) => {
      const template = state.drawingTemplates.find((t) => t.id === templateId);
      if (!template) return {};
      const drawings = state.drawings.map((d) => (d.id === drawingId ? applyLook(d, template.look) : d));
      return { ...remembered(state), drawings, settings: saveWorkspace(state, { drawings }) };
    }),

  toggleDefaultTemplate: (templateId) =>
    set((state) => {
      const target = state.drawingTemplates.find((t) => t.id === templateId);
      if (!target) return {};
      // По умолчанию у инструмента — один шаблон.
      const drawingTemplates = state.drawingTemplates.map((t) =>
        t.kind !== target.kind ? t : { ...t, isDefault: t.id === templateId ? !t.isDefault : false },
      );
      return { drawingTemplates, settings: saveWorkspace(state, { drawingTemplates }) };
    }),

  removeDrawingTemplate: (templateId) =>
    set((state) => {
      const drawingTemplates = state.drawingTemplates.filter((t) => t.id !== templateId);
      return { drawingTemplates, settings: saveWorkspace(state, { drawingTemplates }) };
    }),

  rememberDrawings: () => set((state) => remembered(state)),

  undoDrawings: () =>
    set((state) => {
      const previous = state.drawingPast[state.drawingPast.length - 1];
      if (!previous) return {};
      const drawings = [...previous];
      const priceAlerts = resyncDrawingAlerts(state.priceAlerts, drawings, marketOf(state));
      return {
        drawings,
        priceAlerts,
        drawingPast: state.drawingPast.slice(0, -1),
        drawingFuture: [...state.drawingFuture, state.drawings],
        selectedDrawingId: drawings.some((d) => d.id === state.selectedDrawingId) ? state.selectedDrawingId : null,
        settings: saveWorkspace(state, { drawings, alerts: priceAlerts }),
      };
    }),

  redoDrawings: () =>
    set((state) => {
      const next = state.drawingFuture[state.drawingFuture.length - 1];
      if (!next) return {};
      const drawings = [...next];
      const priceAlerts = resyncDrawingAlerts(state.priceAlerts, drawings, marketOf(state));
      return {
        drawings,
        priceAlerts,
        drawingFuture: state.drawingFuture.slice(0, -1),
        drawingPast: [...state.drawingPast, state.drawings].slice(-HISTORY_LIMIT),
        settings: saveWorkspace(state, { drawings, alerts: priceAlerts }),
      };
    }),

  copyDrawing: (id) =>
    set((state) => {
      const drawing = state.drawings.find((d) => d.id === id);
      if (!drawing) return {};
      return { drawingClipboard: { drawing, price: state.candles[state.candles.length - 1]?.close ?? null } };
    }),

  pasteDrawing: () =>
    set((state) => {
      const clip = state.drawingClipboard;
      if (!clip) return {};
      /*
       * На ту же монету — копия сдвинута на пару свечей, чтобы не легла
       * ровно поверх. На другую — цены пересчитаны в её масштаб: уровень
       * «на 3% выше цены» остаётся на 3% выше, а не на цене другой монеты.
       */
      const same = clip.drawing.venue === state.venue && clip.drawing.symbol === state.symbol;
      const last = state.candles[state.candles.length - 1];
      const step = state.candles.length > 1 ? state.candles[1]!.time - state.candles[0]!.time : 60_000;
      const factor = !same && clip.price && last ? last.close / clip.price : 1;
      const shift = same ? 3 * step : 0;
      const drawing: Drawing = {
        ...withoutAlert(clip.drawing),
        id: nextDrawingId(clip.drawing.kind),
        venue: state.venue,
        symbol: state.symbol,
        locked: false,
        points: clip.drawing.points.map((point) => ({ time: point.time + shift, price: point.price * factor })),
      };
      const drawings = [...state.drawings, drawing];
      const priceAlerts = syncDrawingAlerts(state.priceAlerts, [drawing], [], marketOf(state));
      return {
        ...remembered(state),
        drawings,
        priceAlerts,
        selectedDrawingId: drawing.id,
        settings: saveWorkspace(state, { drawings, alerts: priceAlerts }),
      };
    }),

  duplicateDrawing: (id, shift) => {
    const state = get();
    const source = state.drawings.find((d) => d.id === id);
    if (!source) return null;
    const copy: Drawing = {
      ...withoutAlert(source),
      id: nextDrawingId(source.kind),
      locked: false,
      points: source.points.map((point) => ({
        time: point.time + (shift?.time ?? 0),
        price: point.price + (shift?.price ?? 0),
      })),
    };
    set((current) => {
      const drawings = [...current.drawings, copy];
      const priceAlerts = syncDrawingAlerts(current.priceAlerts, [copy], [], marketOf(current));
      return {
        ...remembered(current),
        drawings,
        priceAlerts,
        selectedDrawingId: copy.id,
        settings: saveWorkspace(current, { drawings, alerts: priceAlerts }),
      };
    });
    return copy.id;
  },

  reorderDrawing: (id, to) =>
    set((state) => {
      const drawing = state.drawings.find((d) => d.id === id);
      if (!drawing) return {};
      const rest = state.drawings.filter((d) => d.id !== id);
      const drawings = to === 'front' ? [...rest, drawing] : [drawing, ...rest];
      return { ...remembered(state), drawings, settings: saveWorkspace(state, { drawings }) };
    }),

  nudgeDrawing: (id, time, price) =>
    set((state) => {
      const drawings = state.drawings.map((drawing) =>
        drawing.id === id && drawing.locked !== true
          ? { ...drawing, points: drawing.points.map((point) => ({ time: point.time + time, price: point.price + price })) }
          : drawing,
      );
      const moved = drawings.find((drawing) => drawing.id === id);
      const priceAlerts = moved ? syncDrawingAlerts(state.priceAlerts, [moved], [], marketOf(state)) : state.priceAlerts;
      return { ...remembered(state), drawings, priceAlerts, settings: saveWorkspace(state, { drawings, alerts: priceAlerts }) };
    }),

  setStickyTool: (stickyTool) => set({ stickyTool }),
  openDrawingMenu: (drawingMenu) => set({ drawingMenu }),

  addDrawing: (drawing, keepTool = false) =>
    set((state) => {
      const drawings = [...state.drawings, drawing];
      const priceAlerts = syncDrawingAlerts(state.priceAlerts, [drawing], [], marketOf(state));
      // Закреплённый инструмент не отпускается: ставят уровень за уровнем.
      keepTool = keepTool || state.stickyTool;
      return {
        ...remembered(state),
        drawings,
        priceAlerts,
        // После рисования возвращаемся к курсору — как в любом
        // редакторе: иначе следующий клик начинал бы новую фигуру.
        // Кисть — исключение: ею рисуют мазок за мазком, и сбрасывать
        // её после каждого значит мешать работать.
        tool: keepTool ? state.tool : ('cursor' as ToolId),
        selectedDrawingId: keepTool ? state.selectedDrawingId : drawing.id,
        // Фигуру с надписью набирают сразу — полем на самом графике.
        textEdit: needsTextWindow(drawing.kind) ? drawing.id : state.textEdit,
        settings: saveWorkspace(state, { drawings, alerts: priceAlerts }),
      };
    }),

  updateDrawing: (id, points) =>
    set((state) => {
      const drawings = state.drawings.map((drawing) =>
        drawing.id === id ? { ...drawing, points: [...points] } : drawing,
      );
      // Сдвинули зону или линию — оповещение едет за ней.
      const moved = drawings.find((drawing) => drawing.id === id);
      const priceAlerts = moved ? syncDrawingAlerts(state.priceAlerts, [moved], [], marketOf(state)) : state.priceAlerts;
      return { drawings, priceAlerts, settings: saveWorkspace(state, { drawings, alerts: priceAlerts }) };
    }),

  poseDrawing: (id, pose) =>
    set((state) => {
      const drawings = state.drawings.map((drawing) => (drawing.id === id ? { ...drawing, pose } : drawing));
      return { drawings, settings: saveWorkspace(state, { drawings }) };
    }),

  restyleDrawing: (id, patch) =>
    set((state) => {
      const drawings = state.drawings.map((drawing) => {
        if (drawing.id !== id) return drawing;
        const next = { ...drawing, ...patch };
        // Снять оповещение — `alert: undefined` в правке: поле уходит целиком.
        if ('alert' in patch && patch.alert === undefined) delete (next as { alert?: unknown }).alert;
        if ('trade' in patch && patch.trade === undefined) delete (next as { trade?: unknown }).trade;
        return next;
      });
      // Поменяли условие, ширину канала регрессии — оповещение следом.
      const changed = drawings.find((drawing) => drawing.id === id);
      const priceAlerts = changed ? syncDrawingAlerts(state.priceAlerts, [changed], [], marketOf(state)) : state.priceAlerts;
      return {
        ...remembered(state),
        drawings,
        priceAlerts,
        settings: saveWorkspace(state, { drawings, alerts: priceAlerts }),
      };
    }),

  removeDrawing: (id) =>
    set((state) => {
      const drawings = state.drawings.filter((drawing) => drawing.id !== id);
      // Удалили рисунок — оповещение уходит вместе с ним.
      const priceAlerts = syncDrawingAlerts(state.priceAlerts, [], [id], marketOf(state));
      return {
        ...remembered(state),
        drawings,
        priceAlerts,
        selectedDrawingId: state.selectedDrawingId === id ? null : state.selectedDrawingId,
        settings: saveWorkspace(state, { drawings, alerts: priceAlerts }),
      };
    }),

  // Одиночный выбор снимает рамку: иначе щелчок по фигуре двигал бы
  // заодно и выделенные раньше.
  selectDrawing: (selectedDrawingId) => set({ selectedDrawingId, selectedDrawingIds: [] }),

  selectedDrawingIds: [],
  selectDrawings: (ids) => set({ selectedDrawingIds: [...ids], selectedDrawingId: null }),

  removeDrawings: (ids) =>
    set((state) => {
      const gone = new Set(ids);
      const drawings = state.drawings.filter((drawing) => !gone.has(drawing.id) || drawing.locked === true);
      const removed = state.drawings.filter((drawing) => !drawings.includes(drawing)).map((drawing) => drawing.id);
      const priceAlerts = syncDrawingAlerts(state.priceAlerts, [], removed, marketOf(state));
      return {
        ...remembered(state),
        drawings,
        priceAlerts,
        selectedDrawingIds: [],
        settings: saveWorkspace(state, { drawings, alerts: priceAlerts }),
      };
    }),

  moveDrawings: (moves) =>
    set((state) => {
      const byId = new Map(moves.map((move) => [move.id, move.points]));
      const drawings = state.drawings.map((drawing) => {
        const points = byId.get(drawing.id);
        return points && drawing.locked !== true ? { ...drawing, points: [...points] } : drawing;
      });
      const moved = drawings.filter((drawing) => byId.has(drawing.id));
      const priceAlerts = syncDrawingAlerts(state.priceAlerts, moved, [], marketOf(state));
      return { drawings, priceAlerts, settings: saveWorkspace(state, { drawings, alerts: priceAlerts }) };
    }),

  clearDrawings: () =>
    set((state) => {
      // Чужую разметку не трогаем: удаление относится к открытому
      // инструменту, а не ко всему профилю.
      const drawings = state.drawings.filter(
        (drawing) => drawing.venue !== state.venue || drawing.symbol !== state.symbol,
      );
      const gone = state.drawings.filter((drawing) => !drawings.includes(drawing)).map((drawing) => drawing.id);
      const priceAlerts = syncDrawingAlerts(state.priceAlerts, [], gone, marketOf(state));
      return {
        ...remembered(state),
        drawings,
        priceAlerts,
        selectedDrawingId: null,
        settings: saveWorkspace(state, { drawings, alerts: priceAlerts }),
      };
    }),

  toggleDrawingFlag: (flag) => set((state) => ({ [flag]: !state[flag] }) as Partial<TerminalState>),
  setStats: (stats) => set({ stats }),
  setConnection: (connection) => set({ connection }),
  setError: (error) => set({ error, load: error ? 'error' : get().load }),

  resetSeries: (candles, gaps) =>
    set((state) => ({
      candles,
      gaps,
      /*
       * Формирующаяся свеча сбрасывается вместе с историей.
       *
       * Пробовал её сохранять — чтобы при возврате на график не ждать
       * следующего тика. Вид графика от этого ломался: на перерисовке
       * ряд оставался пустым, и человек видел чистое поле вместо свечей.
       * Выигрыш был в доли секунды, цена — сломанный экран, и обмен
       * того не стоит.
       *
       * Ждать теперь и не приходится: поток живёт на уровне сеанса и
       * переходы между разделами его не рвут, так что следующий тик
       * приходит сразу, а не через двадцать секунд, как раньше.
       */
      partial: null,
      load: 'ready',
      error: null,
      op: { kind: 'reset', candles },
      opSeq: state.opSeq + 1,
    })),

  prependHistory: (older, gaps) =>
    set((state) => {
      if (older.length === 0) {
        return { loadingHistory: false, historyExhausted: true };
      }
      const oldestKnown = state.candles[0]?.time ?? Number.POSITIVE_INFINITY;
      const fresh = older.filter((candle) => candle.time < oldestKnown);
      if (fresh.length === 0) {
        return { loadingHistory: false, historyExhausted: true };
      }
      const candles = [...fresh, ...state.candles];
      return {
        candles,
        gaps: mergeGaps(state.gaps, gaps),
        loadingHistory: false,
        op: { kind: 'reset', candles: withPartial(candles, state.partial) },
        opSeq: state.opSeq + 1,
      };
    }),

  applyClosed: (candle) =>
    set((state) => {
      const last = state.candles[state.candles.length - 1];
      if (last && candle.time <= last.time) {
        // Бар не новее последнего — это ревизия, а не продолжение ряда.
        return applyRevisionState(state, candle);
      }
      if (state.partial && state.partial.time > candle.time) {
        // На графике уже рисуется бар новее этого: точечно дописать хвост
        // нельзя — библиотека графика падает на свече старше последней.
        const candles = [...state.candles, candle];
        return { candles, op: { kind: 'reset', candles: withPartial(candles, state.partial) }, opSeq: state.opSeq + 1 };
      }
      return {
        candles: [...state.candles, candle],
        // Закрытый бар вытесняет формирующийся: тот стал этим.
        partial: null,
        op: { kind: 'append', candle },
        opSeq: state.opSeq + 1,
      };
    }),

  applyRevision: (candle) => set((state) => applyRevisionState(state, candle)),

  applyPartial: (candle) =>
    set((state) => {
      // Опоздавший формирующийся бар: его время уже закрыто или на графике
      // бар новее. Рисовать его некуда, и график упал бы на нём.
      const last = state.candles[state.candles.length - 1];
      if ((last && candle.time < last.time) || (state.partial && candle.time < state.partial.time)) return {};
      return {
        partial: candle,
        op: { kind: 'live', candle },
        opSeq: state.opSeq + 1,
      };
    }),

  applyGap: (gap) =>
    set((state) => ({ gaps: mergeGaps(state.gaps, [gap]) })),

  setLoadingHistory: (loadingHistory) => set({ loadingHistory }),

  addIndicator: (definitionId) =>
    set((state) => {
      const instance = createInstance(definitionId);
      if (!instance) return {};
      return applyIndicators(state, [...state.indicators, instance]);
    }),

  removeIndicator: (instanceId) =>
    set((state) => {
      const indicators = state.indicators.filter((i) => i.instanceId !== instanceId);
      /*
       * Подписки уходят вместе с индикатором.
       *
       * Оставить их значило бы слать уведомления по тому, чего на
       * графике больше нет: человек убрал индикатор и вправе считать,
       * что убрал целиком.
       */
      const watches = state.watches.filter((watch) => !watch.id.startsWith(`${instanceId}:`));
      const divergenceWatches = state.divergenceWatches.filter(
        (watch) => !watch.id.startsWith(`${instanceId}:`),
      );
      const indicatorWatches = state.indicatorWatches.filter(
        (watch) => !watch.id.startsWith(`${instanceId}:`),
      );

      return {
        indicators,
        watches,
        divergenceWatches,
        indicatorWatches,
        settings: saveWorkspace(state, { indicators, watches, divergenceWatches, indicatorWatches }),
      };
    }),

  updateIndicatorParams: (instanceId, params) =>
    set((state) => {
      const indicators = state.indicators.map((i) =>
        i.instanceId === instanceId ? { ...i, params: { ...i.params, ...params } } : i,
      );

      /*
       * Слепок подписки идёт следом за настройками.
       *
       * Иначе человек поднял бы порог на графике, отметок стало бы
       * меньше, а уведомления продолжали бы приходить по прежнему — и
       * понять расхождение было бы неоткуда.
       */
      const instance = indicators.find((i) => i.instanceId === instanceId);
      const definition = instance ? findIndicator(instance.definitionId) : null;
      const mine = [...state.watches, ...state.divergenceWatches, ...state.indicatorWatches].filter((watch) =>
        watch.id.startsWith(`${instanceId}:`),
      );

      if (!instance || !definition || mine.length === 0) {
        return { indicators, settings: saveWorkspace(state, { indicators }) };
      }

      const clean = sanitizeParams(definition, instance.params);

      if (definition.id in INDICATOR_EVENTS) {
        const indicatorWatches = state.indicatorWatches.map((watch) =>
          watch.id.startsWith(`${instanceId}:`) ? { ...watch, params: clean } : watch,
        );
        return {
          indicators,
          indicatorWatches,
          settings: saveWorkspace(state, { indicators, indicatorWatches }),
        };
      }

      if (definition.id === DIVERGENCE_ID) {
        const options = divergenceWatchSnapshot(clean);
        const divergenceWatches = state.divergenceWatches.map((watch) =>
          watch.id.startsWith(`${instanceId}:`) ? { ...watch, options } : watch,
        );
        return {
          indicators,
          divergenceWatches,
          settings: saveWorkspace(state, { indicators, divergenceWatches }),
        };
      }

      const snapshot = zonesWatchSnapshot(clean);
      const watches = state.watches.map((watch) =>
        watch.id.startsWith(`${instanceId}:`) ? { ...watch, ...snapshot } : watch,
      );

      return { indicators, watches, settings: saveWorkspace(state, { indicators, watches }) };
    }),

  updateIndicatorColor: (instanceId, outputKey, color) =>
    set((state) =>
      applyIndicators(
        state,
        state.indicators.map((i) =>
          i.instanceId === instanceId
            ? { ...i, colors: { ...i.colors, [outputKey]: color } }
            : i,
        ),
      ),
    ),

  /*
   * Сброс — одно изменение, а не два десятка.
   *
   * Настроек у крупного индикатора больше двадцати, и возврат к
   * умолчаниям по одной означал бы столько же записей в профиль и
   * столько же пересчётов графика подряд. Цвета обнуляются вместе с
   * ними: пустой словарь и есть «как было», реестр подставит умолчания
   * определения сам.
   */
  resetIndicator: (instanceId) =>
    set((state) => {
      const target = state.indicators.find((i) => i.instanceId === instanceId);
      const definition = target ? findIndicator(target.definitionId) : null;
      if (!definition) return {};

      return applyIndicators(
        state,
        state.indicators.map((i) =>
          i.instanceId === instanceId
            ? { ...i, params: defaultParams(definition), colors: {} }
            : i,
        ),
      );
    }),

  toggleIndicator: (instanceId) =>
    set((state) =>
      applyIndicators(
        state,
        state.indicators.map((i) =>
          i.instanceId === instanceId ? { ...i, visible: !i.visible } : i,
        ),
      ),
    ),

  rememberPaneShares: (shares) =>
    set((state) => {
      let changed = false;
      const indicators = state.indicators.map((instance) => {
        const share = shares[instance.instanceId];
        if (share === undefined || share === instance.paneShare) return instance;
        changed = true;
        return { ...instance, paneShare: share };
      });

      // Ничего не сдвинулось — не писать: обычное движение мышью по
      // графику не должно порождать запись профиля.
      if (!changed) return {};

      return { indicators, settings: saveWorkspace(state, { indicators }) };
    }),

  toggleZonesWatch: (instanceId, timeframe) =>
    set((state) => {
      const instance = state.indicators.find((i) => i.instanceId === instanceId);
      if (!instance) return {};

      const definition = findIndicator(instance.definitionId);
      if (!definition) return {};

      const id = watchId(instanceId, state.venue, state.symbol, timeframe);
      const existing = state.watches.some((watch) => watch.id === id);
      const watches = existing
        ? state.watches.filter((watch) => watch.id !== id)
        : [
            ...state.watches,
            {
              id,
              venue: state.venue,
              symbol: state.symbol,
              timeframe,
              ...zonesWatchSnapshot(sanitizeParams(definition, instance.params)),
              createdAt: Date.now(),
            },
          ];

      return { watches, settings: saveWorkspace(state, { watches }) };
    }),

  removeZonesWatch: (watchId_) =>
    set((state) => {
      const watches = state.watches.filter((watch) => watch.id !== watchId_);
      if (watches.length === state.watches.length) return {};
      return { watches, settings: saveWorkspace(state, { watches }) };
    }),

  toggleDivergenceWatch: (instanceId, timeframe) =>
    set((state) => {
      const instance = state.indicators.find((i) => i.instanceId === instanceId);
      if (!instance) return {};

      const definition = findIndicator(instance.definitionId);
      if (!definition) return {};

      const id = divergenceWatchId(instanceId, state.venue, state.symbol, timeframe);
      const existing = state.divergenceWatches.some((watch) => watch.id === id);
      const divergenceWatches = existing
        ? state.divergenceWatches.filter((watch) => watch.id !== id)
        : [
            ...state.divergenceWatches,
            {
              id,
              venue: state.venue,
              symbol: state.symbol,
              timeframe,
              options: divergenceWatchSnapshot(sanitizeParams(definition, instance.params)),
              createdAt: Date.now(),
            },
          ];

      return { divergenceWatches, settings: saveWorkspace(state, { divergenceWatches }) };
    }),

  toggleIndicatorWatch: (instanceId, timeframe) =>
    set((state) => {
      const instance = state.indicators.find((i) => i.instanceId === instanceId);
      if (!instance) return {};
      const definition = findIndicator(instance.definitionId);
      if (!definition || !(definition.id in INDICATOR_EVENTS)) return {};

      const id = indicatorWatchId(instanceId, state.venue, state.symbol, timeframe);
      const existing = state.indicatorWatches.some((watch) => watch.id === id);
      const indicatorWatches = existing
        ? state.indicatorWatches.filter((watch) => watch.id !== id)
        : [
            ...state.indicatorWatches,
            {
              id,
              indicator: definition.id,
              venue: state.venue,
              symbol: state.symbol,
              timeframe,
              params: sanitizeParams(definition, instance.params),
              createdAt: Date.now(),
            },
          ];
      return { indicatorWatches, settings: saveWorkspace(state, { indicatorWatches }) };
    }),

  removeIndicatorWatch: (watchId_) =>
    set((state) => {
      const indicatorWatches = state.indicatorWatches.filter((watch) => watch.id !== watchId_);
      if (indicatorWatches.length === state.indicatorWatches.length) return {};
      return { indicatorWatches, settings: saveWorkspace(state, { indicatorWatches }) };
    }),

  removeDivergenceWatch: (watchId_) =>
    set((state) => {
      const divergenceWatches = state.divergenceWatches.filter((watch) => watch.id !== watchId_);
      if (divergenceWatches.length === state.divergenceWatches.length) return {};
      return { divergenceWatches, settings: saveWorkspace(state, { divergenceWatches }) };
    }),
  savePriceAlert: (alert) =>
    set((state) => {
      const exists = state.priceAlerts.some((item) => item.id === alert.id);
      const priceAlerts = exists
        ? state.priceAlerts.map((item) => (item.id === alert.id ? alert : item))
        : [...state.priceAlerts, alert];
      return { priceAlerts, alertDraft: null, settings: saveWorkspace(state, { alerts: priceAlerts }) };
    }),

  togglePriceAlert: (id) =>
    set((state) => {
      const priceAlerts = state.priceAlerts.map((item) =>
        item.id === id
          ? {
              ...item,
              active: !item.active,
              /*
               * Запущенное заново — как новое.
               *
               * Однократное оповещение, которое уже сработало, иначе
               * сервер счёл бы исполненным и молчал: он сверяет время
               * срабатывания с временем создания.
               */
              ...(!item.active ? { createdAt: Date.now() } : {}),
            }
          : item,
      );
      return { priceAlerts, settings: saveWorkspace(state, { alerts: priceAlerts }) };
    }),

  removePriceAlert: (id) =>
    set((state) => {
      const priceAlerts = state.priceAlerts.filter((item) => item.id !== id);
      if (priceAlerts.length === state.priceAlerts.length) return {};
      return { priceAlerts, settings: saveWorkspace(state, { alerts: priceAlerts }) };
    }),

  editPriceAlert: (draft) => set({ alertDraft: draft }),

  setAlertsPanel: (patch) =>
    set((state) => {
      const alertsPanel = { ...state.alertsPanel, ...patch };
      saveAlertsPanel(alertsPanel);
      return { alertsPanel };
    }),

  setSignals: (signals) => set({ signals }),

  applySignal: (signal) =>
    set((state) => {
      // Журнал — по всем монетам: сработавшее оповещение попадает туда,
      // какой бы инструмент ни был открыт.
      const alertLog = isAlertEvent(signal) ? mergeAlertLog(state.alertLog, [signal]) : state.alertLog;
      // Сигналы приходят по инструменту целиком, а показывается один:
      // чужие отбрасываем на входе, чтобы лента не росла бесконечно.
      if (signal.venue !== state.venue || signal.symbol !== state.symbol) return { alertLog };
      return { signals: mergeSignal(state.signals, signal), alertLog };
    }),

  setAlertLog: (signals) => set((state) => ({ alertLog: mergeAlertLog(state.alertLog, signals) })),

  selectSignal: (selectedSignalId) => set({ selectedSignalId }),

  setBots: (bots) => set({ bots }),

  applyBotStatus: (botId, status) =>
    set((state) => ({
      bots: state.bots.map((bot) =>
        bot.manifest.id === botId ? { ...bot, status } : bot,
      ),
    })),

  hydrateSettings: (settings) => set({ settings: normalizeSettings(settings) }),

  setSyncState: (syncState) => set({ syncState }),

  setSyncNotice: (syncNotice) => set({ syncNotice }),

  saveNow: () => {
    flushNow?.();
  },

  patchSettings: (patch) =>
    set((state) => {
      const settings = normalizeSettings(patch(state.settings));
      // Пишем на сервер только пользовательские правки: приход с сервера
      // не должен тут же уезжать обратно.
      persist?.(settings);
      return { settings };
    }),

  markProfile: (patch) => {
    const marks = { ...NO_MARKS, ...get().settings.marks };
    const next = { ...marks };
    let changed = false;
    for (const key of Object.keys(patch) as Array<keyof ProfileMarks>) {
      const value = patch[key];
      if (value !== undefined && value > next[key]) {
        next[key] = value;
        changed = true;
      }
    }
    // Ничего не сдвинулось — и писать нечего: каждая запись двигает версию профиля.
    if (changed) get().patchSettings((current) => ({ ...current, marks: next }));
  },
}));

/** Пороги для конкретного сигнала — с учётом наследования из профиля. */
export function thresholdsFor(
  settings: UserSettings,
  botId: string,
  symbol: string,
): Thresholds {
  return resolveThresholds(settings.thresholds, botId, symbol);
}

/**
 * Рабочее место — часть профиля (ТЗ 7.4).
 *
 * Индикаторы, стиль графика и разметка уезжают на сервер вместе с
 * остальными настройками, а не в отдельное локальное хранилище.
 */
function buildProfile(
  state: TerminalState,
  patch: Partial<{
    indicators: readonly unknown[];
    chartStyle: string;
    scale: ScaleSettings;
    look: ChartLook;
    drawings: readonly unknown[];
    rules: readonly unknown[];
    signals: readonly unknown[];
    bots: readonly unknown[];
    watches: readonly unknown[];
    divergenceWatches: readonly unknown[];
    indicatorWatches: readonly unknown[];
    drawingTemplates: readonly unknown[];
    alerts: readonly unknown[];
    customColors: readonly string[];
    shownBots: readonly string[];
    studio: StudioProgress;
  }>,
): UserSettings {
  /*
   * К прочитанному дописывается непрочитанное — см. `unread`.
   *
   * Берётся из отдельного списка, а не из записанного профиля: иначе
   * каждое сохранение прибавляло бы по копии.
   */
  const unread = state.unread;
  const settings = normalizeSettings({
    ...state.settings,
    workspace: {
      venue: state.venue,
      symbol: state.symbol,
      timeframe: state.timeframe,
      chartStyle: patch.chartStyle ?? state.chartStyle,
      scale: patch.scale ?? state.scale,
      look: patch.look ?? state.look,
      indicators: [...(patch.indicators ?? state.indicators), ...unread.indicators],
      drawings: [...(patch.drawings ?? state.drawings), ...unread.drawings],
      rules: [...(patch.rules ?? state.legacyRules), ...unread.rules],
      signals: [...(patch.signals ?? state.specs), ...unread.signals],
      bots: [...(patch.bots ?? state.customBots), ...unread.bots],
      watches: [...(patch.watches ?? state.watches), ...unread.watches],
      divergenceWatches: [
        ...(patch.divergenceWatches ?? state.divergenceWatches),
        ...unread.divergenceWatches,
      ],
      indicatorWatches: [
        ...(patch.indicatorWatches ?? state.indicatorWatches),
        ...unread.indicatorWatches,
      ],
      drawingTemplates: [
        ...(patch.drawingTemplates ?? state.drawingTemplates),
        ...unread.drawingTemplates,
      ],
      alerts: [...(patch.alerts ?? state.priceAlerts), ...unread.alerts],
      customColors: [...(patch.customColors ?? state.customColors)],
      shownBots: [...(patch.shownBots ?? state.shownBots)],
      studio: patch.studio ?? state.studioProgress,
      radars: [...state.radars],
      journey: state.journey,
      versions: [...state.versions],
    },
  });

  return settings;
}

function saveWorkspace(
  state: TerminalState,
  patch: Parameters<typeof buildProfile>[1],
): UserSettings {
  const settings = buildProfile(state, patch);

  /*
   * Пока профиль не пришёл — не сохранять.
   *
   * Рабочее место пишется целиком: собранные индикаторы, разметка,
   * правила. В первые доли секунды после запуска всё это ещё пусто —
   * профиль едет с сервера. Любое действие, задевающее рабочее место в
   * это окно (смена таймфрейма, зум, перерисовка шкалы), записало бы
   * пустоту поверх настоящей работы, и человек лишился бы собранного
   * молча и без возможности вернуть.
   *
   * Состояние в памяти при этом обновляется как обычно: не сохраняется
   * только запись на сервер.
   */
  if (state.syncState === 'loading') return settings;

  /*
   * Ничего не изменилось — ничего и не пишется.
   *
   * Каждая запись сдвигает версию профиля, а с ней устаревают все
   * остальные открытые вкладки и устройства: их следующая правка
   * упирается в отказ. Заход на график с тем же инструментом не должен
   * так дёргать телефон, лежащий рядом.
   */
  if (JSON.stringify(settings.workspace) === JSON.stringify(state.settings.workspace)) {
    return state.settings;
  }

  persist?.(settings);
  return settings;
}

/**
 * Записать описания сигналов в профиль.
 *
 * Заодно завершает переезд со старых правил: раз описания сохранены,
 * держать те же условия ещё и в `rules` нельзя — Core прочитал бы их
 * дважды и слал бы по два уведомления на одно событие.
 */
function saveSpecs(state: TerminalState, specs: readonly SignalSpec[]): UserSettings {
  const migrated = new Set(specs.map((spec) => spec.id));
  const legacyRules = state.legacyRules.filter((rule) => !migrated.has(rule.id));

  // Список в состоянии обязан совпасть с записанным: иначе следующее
  // сохранение вернуло бы в профиль то, что мы только что убрали.
  state.legacyRules = legacyRules;

  return saveWorkspace(state, { signals: specs, rules: legacyRules });
}

function applyIndicators(
  state: TerminalState,
  indicators: IndicatorInstance[],
): Partial<TerminalState> {
  return { indicators, settings: saveWorkspace(state, { indicators }) };
}

/** Восстанавливает индикаторы из профиля, отбрасывая неизвестные записи. */
export function indicatorsFromSettings(settings: UserSettings): IndicatorInstance[] {
  // Рабочее место ещё не настраивали — включаем объём, как было до того,
  // как он стал индикатором. Пустой список здесь означал бы, что график
  // молча открылся без панели объёма у всех разом.
  if (settings.workspace === null) {
    const volume = createInstance('volume');
    return volume ? [volume] : [];
  }

  const stored = settings.workspace.indicators ?? [];
  const restored: IndicatorInstance[] = [];

  for (const entry of stored) {
    if (typeof entry !== 'object' || entry === null) continue;
    const candidate = entry as Partial<IndicatorInstance>;
    if (typeof candidate.definitionId !== 'string') continue;

    const definition = findIndicator(candidate.definitionId);
    // Определение могло исчезнуть между версиями — одна устаревшая
    // запись не должна стирать остальные.
    if (!definition) continue;

    restored.push({
      instanceId: candidate.instanceId ?? `${candidate.definitionId}-${restored.length}`,
      definitionId: candidate.definitionId,
      params: sanitizeParams(definition, candidate.params ?? {}),
      colors: candidate.colors ?? {},
      visible: candidate.visible !== false,
      /*
       * Доля панели восстанавливается, если она разумна.
       *
       * Ноль схлопнул бы панель насовсем, а вернуть её было бы нечем:
       * разделитель схлопнутой панели не за что тянуть. Единица не
       * оставила бы места цене. Профиль правят и руками, и записи в нём
       * переживают версии кода.
       */
      ...(typeof candidate.paneShare === 'number' &&
      Number.isFinite(candidate.paneShare) &&
      candidate.paneShare > 0 &&
      candidate.paneShare < 1
        ? { paneShare: candidate.paneShare }
        : {}),
    });
  }
  return restored;
}

function applyRevisionState(
  state: TerminalState,
  candle: Candle,
): Partial<TerminalState> {
  const index = state.candles.findIndex((c) => c.time === candle.time);
  if (index === -1) return {};

  const candles = [...state.candles];
  candles[index] = candle;

  // Пересобрать серию целиком нужно только тогда, когда изменился не
  // последний бар: точечное обновление графика умеет менять лишь хвост.
  // Хвост графика — формирующийся бар, если он новее: тогда правка
  // последнего закрытого бара тоже не хвост.
  const tail = withPartial(candles, state.partial);
  const isLast = tail[tail.length - 1] === candle;
  return {
    candles,
    op: isLast ? { kind: 'live', candle } : { kind: 'reset', candles: tail },
    opSeq: state.opSeq + 1,
  };
}

/** Ряд, как он нарисован на графике: закрытые бары и формирующийся, если он новее. */
function withPartial(candles: readonly Candle[], partial: Candle | null): Candle[] {
  const last = candles[candles.length - 1];
  return partial && (!last || partial.time > last.time) ? [...candles, partial] : [...candles];
}

function mergeGaps(current: readonly GapRange[], incoming: readonly GapRange[]): GapRange[] {
  const merged = [...current];
  for (const gap of incoming) {
    if (!merged.some((g) => g.from === gap.from && g.to === gap.to)) merged.push(gap);
  }
  return merged.sort((a, b) => a.from - b.from);
}

/**
 * Разметка из профиля.
 *
 * Одна испорченная запись не должна стирать остальные: непонятная
 * фигура пропускается, а не роняет весь набор.
 */
/**
 * Правила из профиля.
 *
 * Профиль живёт дольше кода: там может лежать правило, сохранённое
 * прошлой версией. Одна непонятная запись не должна стирать остальные,
 * поэтому здесь проверка полей, а не приведение типа.
 */
/**
 * Боты из профиля.
 *
 * Как и правила, читаются по одному: испорченная запись пропускается, а
 * остальные открываются. Профиль живёт дольше кода, и одна строка
 * прошлой версии не должна стирать всё, что человек собрал.
 */
export function customBotsFromSettings(settings: UserSettings): BotDefinition[] {
  const stored = settings.workspace?.bots ?? [];
  const restored: BotDefinition[] = [];

  for (const entry of stored) {
    if (typeof entry !== 'object' || entry === null) continue;
    const raw = entry as Partial<BotDefinition>;
    if (typeof raw.id !== 'string' || typeof raw.name !== 'string') continue;
    // Бот на сигнале живёт без собственных условий: их посчитал сигнал.
    const source = botSourceFrom(raw.source);
    if (!source && (!raw.when || raw.when.kind !== 'group')) continue;

    restored.push({
      id: raw.id,
      name: raw.name,
      kind: raw.kind === 'template' || raw.kind === 'external' ? raw.kind : 'builder',
      // Источник и порог бота: без них бот на сигнале после перезагрузки
      // превратился бы в бота без условий, который молчит навсегда.
      ...(source ? { source } : {}),
      ...(typeof raw.minScore === 'number' ? { minScore: raw.minScore } : {}),
      ...(raw.template ? { template: raw.template } : {}),
      streams: Array.isArray(raw.streams) ? raw.streams : [],
      when: raw.when ?? { kind: 'group', join: 'and', items: [] },
      ...(raw.exit && raw.exit.kind === 'group' ? { exit: raw.exit } : {}),
      ...(botTradeFrom(raw.trade) ? { trade: botTradeFrom(raw.trade)! } : {}),
      ...(botSignalOf(raw.signal) ? { signal: botSignalOf(raw.signal)! } : {}),
      actions: Array.isArray(raw.actions) ? raw.actions : [],
      risk: typeof raw.risk === 'object' && raw.risk !== null ? raw.risk : {},
      params: typeof raw.params === 'object' && raw.params !== null ? raw.params : {},
      /*
       * Состояние читается как есть, а не сбрасывается в «остановлен».
       *
       * Раньше сбрасывалось: считалось, что открытие страницы не должно
       * ничего запускать. Но запускает не страница — торгует Core, и
       * запускает его сам человек кнопкой. Бот, работающий при закрытой
       * вкладке, — это то, ради чего он и нужен.
       *
       * Показать «остановлен» у работающего бота значило бы соврать о
       * состоянии, а первое же сохранение профиля этой ложью бота бы и
       * выключило. `starting` — состояние перехода, и после перезагрузки
       * его уже нет: сервер либо ведёт бота, либо нет.
       */
      state: raw.state === 'starting' ? 'running' : (raw.state ?? 'stopped'),
      version: typeof raw.version === 'number' ? raw.version : 1,
      createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : 0,
      updatedAt: typeof raw.updatedAt === 'number' ? raw.updatedAt : 0,
    });
  }
  return restored;
}

export function rulesFromSettings(settings: UserSettings): Rule[] {
  const stored = settings.workspace?.rules ?? [];
  const restored: Rule[] = [];

  for (const entry of stored) {
    if (typeof entry !== 'object' || entry === null) continue;
    const raw = entry as Partial<Rule>;
    if (typeof raw.id !== 'string' || typeof raw.symbol !== 'string') continue;

    /*
     * Правило читается в обеих формах.
     *
     * Дерево групп — нынешняя; плоский список — та, в которой лежат
     * правила, созданные раньше. Требовать только одну значило бы молча
     * потерять половину собранного человеком: правило пропадает из
     * списка, и понять почему невозможно.
     */
    const when = raw.when && raw.when.kind === 'group' ? raw.when : null;
    const flat = Array.isArray(raw.conditions) && raw.conditions.length > 0 ? raw.conditions : null;
    if (!when && !flat) continue;

    restored.push({
      id: raw.id,
      name: typeof raw.name === 'string' ? raw.name : 'Правило',
      venue: typeof raw.venue === 'string' ? raw.venue : 'binance',
      symbol: raw.symbol,
      timeframe: raw.timeframe ?? '1H',
      ...(when ? { when } : {}),
      ...(flat ? { conditions: flat } : {}),
      action: raw.action ?? 'alert',
      version: typeof raw.version === 'number' ? raw.version : 1,
      enabled: raw.enabled !== false,
      notify: raw.notify === true,
      createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : 0,
      updatedAt: typeof raw.updatedAt === 'number' ? raw.updatedAt : 0,
    });
  }
  return restored;
}

export function drawingsFromSettings(settings: UserSettings): Drawing[] {
  const stored = settings.workspace?.drawings ?? [];
  const restored: Drawing[] = [];
  for (const entry of stored) {
    const drawing = normalizeDrawing(entry);
    if (drawing) restored.push(drawing);
  }
  return restored;
}

/**
 * Разметка открытого инструмента.
 *
 * В сторе лежат фигуры всех инструментов сразу, и путать их нельзя ни
 * при отрисовке, ни при подсчёте: кнопка «удалить», обещающая стереть
 * чужой уровень, не стёрла бы ничего.
 */
export function ownDrawings(state: {
  drawings: readonly Drawing[];
  venue: string;
  symbol: string;
}): Drawing[] {
  return state.drawings.filter(
    (drawing) => drawing.venue === state.venue && drawing.symbol === state.symbol,
  );
}

/**
 * Описания сигналов из профиля.
 *
 * Читаются оба хранилища: нынешнее и то, где лежат правила, созданные
 * раньше. Правило приводится к описанию `specFromRule` — оно ничего не
 * выдумывает и считается ровно так же, как считалось.
 *
 * Совпадение по идентификатору решается в пользу описания: если правило
 * уже переехало, его старая копия в профиле — остаток переезда, а не
 * второй сигнал.
 */
/**
 * Индикатор внутри бота — тем же разбором, что индикаторы профиля: чего
 * терминал не понял бы как индикатор, того не понимает и внутри бота.
 */
function botSignalOf(raw: unknown): import('@specter/types').BotSignal | null {
  const signal = botSignalFrom(raw);
  if (!signal) return null;
  const [spec] = specsFromSettings({ workspace: { signals: [signal.spec] } } as unknown as UserSettings);
  return spec ? { ...signal, spec } : null;
}

export function specsFromSettings(settings: UserSettings): SignalSpec[] {
  const stored = settings.workspace?.signals ?? [];
  const restored: SignalSpec[] = [];
  const seen = new Set<string>();

  for (const entry of stored) {
    const spec = asSpec(entry);
    if (!spec || seen.has(spec.id)) continue;
    seen.add(spec.id);
    restored.push(spec);
  }

  for (const rule of rulesFromSettings(settings)) {
    if (seen.has(rule.id)) continue;
    seen.add(rule.id);
    restored.push(specFromRule(rule));
  }

  return restored;
}

function asSpec(value: unknown): SignalSpec | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown> & Partial<SignalSpec>;

  if (typeof raw.id !== 'string' || raw.id === '') return null;
  if (typeof raw.venue !== 'string' || typeof raw.symbol !== 'string') return null;

  const branches = branchesOf(raw);
  if (branches.length === 0) return null;
  const trade = normalizeTradePlan(raw.trade);

  return {
    id: raw.id,
    name: typeof raw.name === 'string' ? raw.name : 'Сигнал',
    venue: raw.venue,
    symbol: raw.symbol,
    timeframe: raw.timeframe ?? '1H',
    branches,
    threshold: typeof raw.threshold === 'number' ? raw.threshold : 0,
    version: typeof raw.version === 'number' ? raw.version : 1,
    enabled: raw.enabled !== false,
    notify: raw.notify === true,
    createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : 0,
    updatedAt: typeof raw.updatedAt === 'number' ? raw.updatedAt : 0,
    // План сделки из вкладки «Сделка» — вместе с индикатором, иначе зоны и бот его не увидят.
    ...(trade ? { trade } : {}),
  };
}

/**
 * Ветвь из профиля.
 *
 * Читается и нынешняя форма — список ветвей, — и прежняя, где у
 * описания было одно направление и один набор условий. Профиль живёт
 * дольше кода: сигнал, записанный вчера, обязан открыться сегодня, и
 * открыться тем же, чем был.
 */
function asBranch(value: unknown): SignalBranch | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Partial<SignalBranch>;

  const context = Array.isArray(raw.context) ? raw.context : [];
  const trigger = raw.trigger && typeof raw.trigger === 'object' ? raw.trigger : null;
  const advanced = raw.advanced && raw.advanced.kind === 'group' ? raw.advanced : null;

  /*
   * Ветвь без ворот не читается: считать её не по чему. Пустой контекст
   * при пустом триггере означал бы сигнал, который срабатывает всегда,
   * — а это не то, что человек имел в виду ни в одном мыслимом случае.
   */
  if (context.length === 0 && !trigger && !advanced) return null;

  const confirmations = (Array.isArray(raw.confirmations) ? raw.confirmations : []).filter(
    (entry): entry is SignalConfirmation =>
      typeof entry === 'object' && entry !== null && typeof entry.id === 'string',
  );

  return {
    direction: raw.direction === 'LONG' || raw.direction === 'SHORT' ? raw.direction : 'ALERT',
    context,
    trigger,
    confirmations,
    ...(advanced ? { advanced } : {}),
  };
}

/** Ветви описания в обеих формах: списком и прежней плоской. */
function branchesOf(raw: Record<string, unknown>): SignalBranch[] {
  if (Array.isArray(raw['branches'])) {
    const out: SignalBranch[] = [];
    for (const entry of raw['branches']) {
      const branch = asBranch(entry);
      if (branch) out.push(branch);
    }
    return out;
  }

  // Прежняя форма: направление и условия лежали прямо в описании.
  const single = asBranch(raw);
  return single ? [single] : [];
}

/**
 * Ключ подписки: индикатор и инструмент вместе.
 *
 * Один индикатор человек держит на нескольких рынках, и подписка на
 * биткойн не должна означать подписку на всё сразу. Обратная сторона:
 * убрав индикатор, мы находим его подписки по началу ключа — поэтому
 * разделитель здесь тот, что в идентификаторе экземпляра не
 * встречается.
 */
export function watchId(
  instanceId: string,
  venue: string,
  symbol: string,
  timeframe: Timeframe,
): string {
  return `${instanceId}:${venue}|${symbol}|${timeframe}`;
}

/**
 * Профиль с новым инструментом и таймфреймом — остальное как было.
 *
 * Отдельной функцией, потому что здесь уже терялись данные. Рабочее место
 * пересобиралось по полям: перечислялись индикаторы, стиль, масштаб,
 * разметка, правила и боты — а всё, чего в перечне не было, исчезало при
 * каждой смене таймфрейма. Так пропадали подписки на зоны, подписки на
 * дивергенции и собранные человеком индикаторы: ни ошибки, ни следа,
 * поле просто переставало существовать.
 *
 * Правило простое и проверяется тестом: запись переносится целиком, а
 * переопределяется только названное. Иначе всякое новое поле обречено
 * потеряться ровно здесь.
 */
export function withInstrument(
  current: UserSettings,
  at: { venue: string; symbol: string; timeframe: Timeframe },
): UserSettings {
  return {
    ...current,
    workspace: {
      ...current.workspace,
      venue: at.venue,
      symbol: at.symbol,
      timeframe: at.timeframe,
      indicators: current.workspace?.indicators ?? [],
      chartStyle: current.workspace?.chartStyle ?? DEFAULT_CHART_STYLE,
      scale: parseScale(current.workspace?.scale),
      ...(current.workspace?.look !== undefined ? { look: current.workspace.look } : {}),
      drawings: current.workspace?.drawings ?? [],
      rules: current.workspace?.rules ?? [],
      bots: current.workspace?.bots ?? [],
    },
  };
}

/** Подписки из профиля; непонятные записи отбрасываются. */
export function watchesFromSettings(settings: UserSettings): ZonesWatch[] {
  return zonesWatchesFrom(settings.workspace?.watches);
}

/** Подписки на дивергенции из профиля; непонятные записи отбрасываются. */
export function divergenceWatchesFromSettings(settings: UserSettings): DivergenceWatch[] {
  return divergenceWatchesFrom(settings.workspace?.divergenceWatches);
}

/** Сколько шагов назад помнит Ctrl+Z. */
const HISTORY_LIMIT = 60;

/** Копия рисунка без колокольчика: оповещение вешают осознанно, а не размножают. */
/**
 * Копия фигуры — без оповещения и без сделки «Фибо-пульта»: копия сетки,
 * сама начавшая торговать, поставила бы вторую заявку без спроса.
 */
function withoutAlert(drawing: Drawing): Drawing {
  if (!drawing.alert && !drawing.trade) return drawing;
  const { alert: _alert, trade: _trade, ...rest } = drawing;
  return rest;
}

/** Разметка до правки — в историю; повтор после новой правки теряет смысл. */
function remembered(state: TerminalState): Pick<TerminalState, 'drawingPast' | 'drawingFuture'> {
  return { drawingPast: [...state.drawingPast, state.drawings].slice(-HISTORY_LIMIT), drawingFuture: [] };
}

/** Оповещения рисунков заново по всей разметке — после отмены и повтора. */
function resyncDrawingAlerts(
  alerts: readonly PriceAlert[],
  drawings: readonly Drawing[],
  market?: AlertMarket,
): PriceAlert[] {
  const present = new Set(drawings.map((drawing) => drawing.id));
  const gone = alerts.filter((alert) => alert.drawingId && !present.has(alert.drawingId)).map((alert) => alert.drawingId!);
  return syncDrawingAlerts(alerts, drawings, gone, market);
}

/** Открытый инструмент и его свечи: по ним считается граница канала регрессии. */
export interface AlertMarket {
  readonly venue: string;
  readonly symbol: string;
  readonly candles: readonly Candle[];
}

function marketOf(state: Pick<TerminalState, 'venue' | 'symbol' | 'candles'>): AlertMarket {
  return { venue: state.venue, symbol: state.symbol, candles: state.candles };
}

/**
 * Оповещения, которые задают рисунки.
 *
 * «Моя зона» и линия-оповещение будят всегда — ради этого их и рисуют.
 * Остальные рисунки — линия, уровень, прямоугольник, каналы — будят,
 * если на них повесили оповещение колокольчиком: условие лежит в самом
 * рисунке (`drawing.alert`), а граница берётся из его точек.
 *
 * Рисунок и оповещение — одно целое: подвинули рисунок — оповещение
 * едет за ним, удалили — уходит. Выключенное в панели оповещений
 * остаётся выключенным и после сдвига.
 */
export function syncDrawingAlerts(
  alerts: readonly PriceAlert[],
  changed: readonly Drawing[],
  removed: readonly string[],
  market?: AlertMarket,
): PriceAlert[] {
  const gone = new Set(removed.map((id) => `drawing-${id}`));
  let next = alerts.filter((alert) => !gone.has(alert.id));
  for (const drawing of changed) {
    if (drawing.kind !== 'alert-zone' && drawing.kind !== 'alert-line') {
      next = syncAttachedAlert(next, drawing, market);
      continue;
    }
    const [from, to] = drawing.points;
    if (!from || !to) continue;
    const id = `drawing-${drawing.id}`;
    const known = next.find((alert) => alert.id === id);
    const zone = drawing.kind === 'alert-zone';
    const alert: PriceAlert = {
      id,
      venue: drawing.venue,
      symbol: drawing.symbol,
      price: zone ? (from.price + to.price) / 2 : to.price,
      condition: 'cross',
      repeat: 'every',
      note: zone ? 'Моя зона' : 'Линия-оповещение',
      active: known?.active ?? true,
      createdAt: known?.createdAt ?? Date.now(),
      drawingId: drawing.id,
      ...(zone
        ? { zone: { low: Math.min(from.price, to.price), high: Math.max(from.price, to.price) } }
        : { line: { from: { time: from.time, price: from.price }, to: { time: to.time, price: to.price } } }),
    };
    next = known ? next.map((item) => (item.id === id ? alert : item)) : [...next, alert];
  }
  return next;
}

/** Оповещение, повешенное на обычный рисунок колокольчиком. */
function syncAttachedAlert(alerts: PriceAlert[], drawing: Drawing, market?: AlertMarket): PriceAlert[] {
  const id = `drawing-${drawing.id}`;
  const known = alerts.find((alert) => alert.id === id);
  const shape = alertShapeOf(drawing.kind);
  if (!drawing.alert || !shape) return known ? alerts.filter((alert) => alert.id !== id) : alerts;

  // Свечи — только своего инструмента: чужие дали бы регрессии чужую границу.
  const candles =
    market && market.venue === drawing.venue && market.symbol === drawing.symbol ? market.candles : [];
  const geometry = alertGeometry(drawing, candles);
  // Границу посчитать нечем — оповещение остаётся с прежней.
  if (!geometry) return alerts;

  // Условие поправили — сработавшее однократное снова в деле.
  const renewed = !known || known.createdAt !== drawing.alert.since;
  const alert: PriceAlert = {
    id,
    venue: drawing.venue,
    symbol: drawing.symbol,
    ...geometry,
    condition: conditionFor(shape, drawing.alert.condition),
    repeat: drawing.alert.repeat,
    ...(drawing.alert.note ? { note: drawing.alert.note } : {}),
    active: renewed ? true : known.active,
    createdAt: drawing.alert.since,
    drawingId: drawing.id,
  };
  return known ? alerts.map((item) => (item.id === id ? alert : item)) : [...alerts, alert];
}

/** Подписки на оповещения индикаторов из профиля. */
export function indicatorWatchesFromSettings(settings: UserSettings): IndicatorWatch[] {
  return indicatorWatchesFrom(settings.workspace?.indicatorWatches);
}

type WorkspaceList = keyof UnreadEntries;

/**
 * Прочтёт ли эта версия терминала одну запись списка.
 *
 * Спрашивается тот же разбор, что читает профиль, — на профиле из одной
 * этой записи. Своя проверка рядом разошлась бы с разбором при первой
 * же правке, и запись, которую разбор отбрасывает, не попала бы и сюда.
 */
function readable(
  list: WorkspaceList,
  entry: unknown,
  parse: (settings: UserSettings) => readonly unknown[],
): boolean {
  const probe = normalizeSettings({
    workspace: { venue: 'probe', symbol: 'probe', indicators: [], [list]: [entry] },
  });
  return parse(probe).length > 0;
}

/** Записи профиля, которые эта версия терминала не читает. */
export function unreadFromSettings(settings: UserSettings): UnreadEntries {
  const workspace = settings.workspace;
  if (!workspace) return NO_UNREAD;

  const pick = (
    list: WorkspaceList,
    stored: readonly unknown[] | undefined,
    parse: (settings: UserSettings) => readonly unknown[],
  ): unknown[] => (stored ?? []).filter((entry) => !readable(list, entry, parse));

  return {
    indicators: pick('indicators', workspace.indicators, indicatorsFromSettings),
    drawings: pick('drawings', workspace.drawings, drawingsFromSettings),
    rules: pick('rules', workspace.rules, rulesFromSettings),
    signals: pick('signals', workspace.signals, (probe) =>
      // Только сами описания: правила переезжают в них отдельно.
      specsFromSettings({ ...probe, workspace: { ...probe.workspace!, rules: [] } }),
    ),
    bots: pick('bots', workspace.bots, customBotsFromSettings),
    watches: pick('watches', workspace.watches, watchesFromSettings),
    divergenceWatches: pick(
      'divergenceWatches',
      workspace.divergenceWatches,
      divergenceWatchesFromSettings,
    ),
    indicatorWatches: pick('indicatorWatches', workspace.indicatorWatches, indicatorWatchesFromSettings),
    drawingTemplates: pick('drawingTemplates', workspace.drawingTemplates, (probe) =>
      drawingTemplatesFrom(probe.workspace?.drawingTemplates),
    ),
    alerts: pick('alerts', workspace.alerts, priceAlertsFromSettings),
  };
}

/**
 * Черновик оповещения: то, что заполняется в окне.
 *
 * `id` есть у правки и нет у нового. Цена — строкой: человек вводит её
 * с клавиатуры, и «80 7» — это ещё не число, а уже не пусто.
 */
export interface PriceAlertDraft {
  readonly id?: string;
  readonly venue: string;
  readonly symbol: string;
  readonly price: number;
  readonly condition?: PriceAlert['condition'];
  readonly repeat?: PriceAlert['repeat'];
  readonly note?: string;
}

const ALERTS_PANEL_KEY = 'specter.alertsPanel';

/**
 * Панель по умолчанию свёрнута: график шире, а часики с числом
 * работающих оповещений видны в полосе справа. Раскрыл человек сам —
 * запоминается.
 */
function loadAlertsPanel(): TerminalState['alertsPanel'] {
  try {
    const raw = JSON.parse(localStorage.getItem(ALERTS_PANEL_KEY) ?? 'null') as Partial<
      TerminalState['alertsPanel']
    > | null;
    return {
      // Оповещения теперь окно поверх страницы: открытым при загрузке оно не встаёт, запоминается только вкладка.
      open: false,
      tab: raw?.tab === 'journal' ? 'journal' : 'alerts',
    };
  } catch {
    return { open: false, tab: 'alerts' };
  }
}

function saveAlertsPanel(value: TerminalState['alertsPanel']): void {
  try {
    localStorage.setItem(ALERTS_PANEL_KEY, JSON.stringify(value));
  } catch {
    // Приватное окно: панель просто не запомнит положение.
  }
}

/** Оповещения по цене из профиля. */
export function priceAlertsFromSettings(settings: UserSettings): PriceAlert[] {
  return priceAlertsFrom(settings.workspace?.alerts);
}

/**
 * Поставить в терминал профиль, пришедший с сервера.
 *
 * Одним местом: приложение и проверки идут одним путём. Раньше список
 * разбираемого был перечислен прямо в загрузчике, и забыть в нём одно
 * поле значило потерять его у всех при первом сохранении.
 */
export function applyLoadedProfile(settings: UserSettings): UserSettings {
  useTerminal.getState().hydrateSettings(settings);
  /*
   * Прочитанное на другом устройстве гаснет и здесь. А если здесь
   * отметка новее (прочитали, пока профиль ещё ехал), — она уходит в
   * профиль, чтобы погасло и там.
   */
  const remoteSeen = settings.marks?.notificationsSeenAt ?? 0;
  adoptSeen(remoteSeen);
  const localSeen = seenAt();
  if (localSeen > remoteSeen) queueMicrotask(() => useTerminal.getState().markProfile({ notificationsSeenAt: localSeen }));
  useTerminal.setState({
    indicators: indicatorsFromSettings(settings),
    chartStyle: settings.workspace?.chartStyle ?? DEFAULT_CHART_STYLE,
    scale: parseScale(settings.workspace?.scale),
    look: parseLook(settings.workspace?.look),
    drawings: drawingsFromSettings(settings),
    specs: specsFromSettings(settings),
    // Не переехавшие правила держатся отдельно: профиль пишется
    // целиком, и потерять их значило бы стереть чужую работу.
    legacyRules: rulesFromSettings(settings),
    customBots: customBotsFromSettings(settings),
    watches: watchesFromSettings(settings),
    divergenceWatches: divergenceWatchesFromSettings(settings),
    indicatorWatches: indicatorWatchesFromSettings(settings),
    drawingTemplates: drawingTemplatesFrom(settings.workspace?.drawingTemplates),
    customColors: [...(settings.workspace?.customColors ?? [])],
    shownBots: [...(settings.workspace?.shownBots ?? [])],
    radars: userRadarsFrom(settings.workspace?.radars),
    studioProgress: settings.workspace?.studio ?? NO_STUDIO_PROGRESS,
    journey: journeyFrom(settings.workspace?.journey),
    versions: versionsFrom(settings.workspace?.versions),
    priceAlerts: priceAlertsFromSettings(settings),
    unread: unreadFromSettings(settings),
  });

  // Тот же профиль в том виде, в каком его запишет терминал: от него
  // синхронизация считает, что изменил человек (см. profile-merge).
  return buildProfile(useTerminal.getState(), {});
}

/** Бот для конструктора, собранный из индикатора студии. */
export interface BotDraft {
  readonly name: string;
  readonly symbol: string;
  readonly timeframe: import('@specter/types').Timeframe;
  /** Когда входить — все сразу. */
  readonly entry: readonly StudioCondition[];
  /** Когда выходить — все сразу; пусто — по цели или стопу. */
  readonly exit: readonly StudioCondition[];
  readonly market: 'spot' | 'futures';
  readonly direction: 'long' | 'short' | 'both';
  /** Плечо, с которым студия считала стратегию: конструктор открывается с ним же. */
  readonly leverage: number;
  /** Индикатор целиком: бот входит и выходит по его отметкам. */
  readonly signal: import('@specter/types').BotSignal | null;
  /** Готовая защита — у классических героев: цель, стоп, безубыток, скользящий стоп, %. */
  readonly take?: number;
  readonly stop?: number;
  readonly breakeven?: number;
  readonly trailing?: number;
}

/** Виды, которым окно настроек открывается сразу: без надписи они пусты. */
function needsTextWindow(kind: DrawingKind): boolean {
  return kind === 'trade-plan' || findKind(kind)?.needsText === true;
}

// Прочитанные уведомления — в профиль, чтобы погасли на всех устройствах.
setSeenPersister((at) => useTerminal.getState().markProfile({ notificationsSeenAt: at }));
